import React, { useState, useEffect, useCallback, useRef, memo } from 'react';
import axios from 'axios';
import { 
  Package, Search, Filter, ChevronDown, ChevronUp, Eye, Edit2, RefreshCw,
  CheckCircle, Clock, Truck, RotateCcw, ClipboardCheck, AlertCircle, X,
  User, Mail, Phone, Calendar, MapPin, DollarSign, FileText, History,
  Plus, Minus, Trash2, Save, Send, Download, Settings, Loader2, Boxes,
  ChevronRight, ArrowRight, AlertTriangle, PenTool, ArrowLeftRight, Replace, Check,
  MessageSquare, CreditCard, Camera, Image as ImageIcon, Upload
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  received: { icon: Package, color: 'text-blue-400', bg: 'bg-blue-500/20', border: 'border-blue-500/50', label: 'Received' },
  processing: { icon: Clock, color: 'text-yellow-400', bg: 'bg-yellow-500/20', border: 'border-yellow-500/50', label: 'Processing' },
  ready_for_pickup: { icon: CheckCircle, color: 'text-green-400', bg: 'bg-green-500/20', border: 'border-green-500/50', label: 'Ready' },
  delivered: { icon: Truck, color: 'text-purple-400', bg: 'bg-purple-500/20', border: 'border-purple-500/50', label: 'Delivered' },
  returned: { icon: RotateCcw, color: 'text-orange-400', bg: 'bg-orange-500/20', border: 'border-orange-500/50', label: 'Returned' },
  inspected: { icon: ClipboardCheck, color: 'text-teal-400', bg: 'bg-teal-500/20', border: 'border-teal-500/50', label: 'Inspected' },
  complete: { icon: CheckCircle, color: 'text-emerald-400', bg: 'bg-emerald-500/20', border: 'border-emerald-500/50', label: 'Complete' },
  cancelled: { icon: AlertCircle, color: 'text-red-400', bg: 'bg-red-500/20', border: 'border-red-500/50', label: 'Cancelled' }
};

const STATUSES = ['received', 'processing', 'ready_for_pickup', 'delivered', 'returned', 'inspected', 'complete', 'cancelled'];

// Get next status in the workflow
const getNextStatus = (currentStatus) => {
  const workflowOrder = ['received', 'processing', 'ready_for_pickup', 'delivered', 'returned', 'inspected', 'complete'];
  const currentIndex = workflowOrder.indexOf(currentStatus);
  if (currentIndex === -1 || currentIndex >= workflowOrder.length - 1) return null;
  return workflowOrder[currentIndex + 1];
};

// Separate component to prevent focus issues - completely uncontrolled with ref-based data collection
const LocationRow = memo(({ location, index, onDelete, locationsRef }) => {
  const idRef = useRef(null);
  const nameRef = useRef(null);
  const addressRef = useRef(null);
  const feeRef = useRef(null);

  // Store refs in parent's ref object for data collection on save
  useEffect(() => {
    if (locationsRef.current) {
      locationsRef.current[index] = {
        getId: () => idRef.current?.value || '',
        getName: () => nameRef.current?.value || '',
        getAddress: () => addressRef.current?.value || '',
        getFee: () => parseFloat(feeRef.current?.value) || 0
      };
    }
  }, [index, locationsRef]);

  return (
    <div className="bg-slate-700/50 p-3 rounded-lg mb-3 space-y-2">
      <div className="flex gap-2 items-center">
        <div className="flex-1">
          <Label className="text-slate-400 text-xs mb-1 block">Location ID</Label>
          <input
            ref={idRef}
            placeholder="e.g. main-office"
            defaultValue={location.id || ''}
            className="w-full h-10 px-3 rounded-md bg-slate-700 border border-slate-600 text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
          />
        </div>
        <div className="flex-1">
          <Label className="text-slate-400 text-xs mb-1 block">Name</Label>
          <input
            ref={nameRef}
            placeholder="e.g. Main Office"
            defaultValue={location.name || ''}
            className="w-full h-10 px-3 rounded-md bg-slate-700 border border-slate-600 text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
          />
        </div>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => onDelete(index)}
          className="text-red-400 mt-5 shrink-0"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
      <div className="flex gap-2 items-center">
        <div className="flex-1">
          <Label className="text-slate-400 text-xs mb-1 block">Address</Label>
          <input
            ref={addressRef}
            placeholder="Full address"
            defaultValue={location.address || ''}
            className="w-full h-10 px-3 rounded-md bg-slate-700 border border-slate-600 text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
          />
        </div>
        <div className="w-28 shrink-0">
          <Label className="text-slate-400 text-xs mb-1 block">Delivery Fee</Label>
          <div className="flex items-center">
            <span className="text-slate-400 text-sm mr-1">$</span>
            <input
              ref={feeRef}
              type="number"
              step="0.01"
              min="0"
              placeholder="0.00"
              defaultValue={location.delivery_fee || ''}
              className="w-full h-10 px-3 rounded-md bg-slate-700 border border-slate-600 text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>
        </div>
      </div>
    </div>
  );
});

const RentalManagement = ({ open, onClose, fullPage = false }) => {
  // Main tab
  const [mainTab, setMainTab] = useState('orders');
  
  // Data
  const [orders, setOrders] = useState([]);
  const [settings, setSettings] = useState(null);
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingInventory, setLoadingInventory] = useState(false);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [inventorySearch, setInventorySearch] = useState('');
  
  // Selected order
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderDetail, setShowOrderDetail] = useState(false);
  
  // Settings dialog
  const [showSettings, setShowSettings] = useState(false);
  const [editingSettings, setEditingSettings] = useState(null);
  const locationRefsRef = useRef({}); // Ref to store location input refs
  
  // Inventory editing
  const [editingInventory, setEditingInventory] = useState(null);
  const [savingInventory, setSavingInventory] = useState(false);
  const [inventoryCheckDate, setInventoryCheckDate] = useState('');
  const [uploadingImage, setUploadingImage] = useState(null); // SKU of item being uploaded
  const [imageMenuOpen, setImageMenuOpen] = useState(null); // SKU of item with open menu
  const cameraInputRef = useRef(null);
  const fileInputRef = useRef(null);
  const currentUploadSku = useRef(null);
  
  // Add rental product
  const [showAddProduct, setShowAddProduct] = useState(false);
  const [newProduct, setNewProduct] = useState({ name: '', price: '', description: '', sku: '' });
  const [savingProduct, setSavingProduct] = useState(false);
  
  // Manual rental order
  const [showManualOrder, setShowManualOrder] = useState(false);
  const [manualOrder, setManualOrder] = useState({
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    instructorId: '',
    pickupDate: '',
    pickupTime: '09:00',
    returnDate: '',
    returnTime: '17:00',
    pickupLocation: '',
    items: [],
    notes: '',
    paymentMethod: 'cash',
    depositCollected: true,
    depositExempt: false,  // If true, no deposit required
    customDeposit: 0  // Custom deposit amount, defaults to 0
  });
  const [availableProducts, setAvailableProducts] = useState([]);
  const [availableInstructors, setAvailableInstructors] = useState([]);
  const [savingManualOrder, setSavingManualOrder] = useState(false);
  
  // Status update
  const [showStatusUpdate, setShowStatusUpdate] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [statusNotes, setStatusNotes] = useState('');
  
  // Refund dialog
  const [showRefundDialog, setShowRefundDialog] = useState(false);
  const [refundType, setRefundType] = useState('full');
  const [refundAmount, setRefundAmount] = useState(0);
  const [refundNotes, setRefundNotes] = useState('');
  
  // Items edit
  const [editingItems, setEditingItems] = useState(false);
  const [tempItems, setTempItems] = useState([]);
  
  // Processing states
  const [processingStatus, setProcessingStatus] = useState(false);
  const [processingRefund, setProcessingRefund] = useState(false);
  const [savingItems, setSavingItems] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  
  // Quick status advance
  const [quickStatusOrder, setQuickStatusOrder] = useState(null);
  const [quickStatusNotes, setQuickStatusNotes] = useState('');
  const [showQuickStatusModal, setShowQuickStatusModal] = useState(false);
  const [processingQuickStatus, setProcessingQuickStatus] = useState(false);
  
  // Inspection modal
  const [showInspectionModal, setShowInspectionModal] = useState(false);
  const [inspectionOrder, setInspectionOrder] = useState(null);
  const [inspectionItems, setInspectionItems] = useState([]);
  const [inspectionNotes, setInspectionNotes] = useState('');
  const [processingInspection, setProcessingInspection] = useState(false);
  const [inspectorSignature, setInspectorSignature] = useState(null);
  const [showInspectorSignatureModal, setShowInspectorSignatureModal] = useState(false);
  const inspectorSigCanvasRef = useRef(null);
  const [isDrawingInspectorSig, setIsDrawingInspectorSig] = useState(false);
  const [additionalFees, setAdditionalFees] = useState([]);  // {description: '', amount: 0}
  
  // Fulfillment checkpoint (Processing → Ready)
  const [showFulfillmentModal, setShowFulfillmentModal] = useState(false);
  const [fulfillmentOrder, setFulfillmentOrder] = useState(null);
  const [fulfillmentItems, setFulfillmentItems] = useState([]);
  const [fulfillmentNote, setFulfillmentNote] = useState('');
  const [processingFulfillment, setProcessingFulfillment] = useState(false);
  const [replacementItems, setReplacementItems] = useState([]); // Replacement items for shortages
  const [showReplacementPicker, setShowReplacementPicker] = useState(false);
  const [replacementForSku, setReplacementForSku] = useState(null); // Which item we're replacing
  const [availableReplacements, setAvailableReplacements] = useState([]); // Available rental products
  
  // Delivery checkpoint (Ready → Delivered)
  const [showDeliveryModal, setShowDeliveryModal] = useState(false);
  const [deliveryOrder, setDeliveryOrder] = useState(null);
  const [deliverySignature, setDeliverySignature] = useState(null);
  const [deliveryAgreed, setDeliveryAgreed] = useState(false);
  const [processingDelivery, setProcessingDelivery] = useState(false);
  const [deliveryConditionNotes, setDeliveryConditionNotes] = useState('');
  const [deliveryItemConditions, setDeliveryItemConditions] = useState({});
  
  // Full-screen signature modal
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  
  // Signature canvas ref
  const signatureCanvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  
  // Edit schedule dialog
  const [showEditSchedule, setShowEditSchedule] = useState(false);
  const [editScheduleData, setEditScheduleData] = useState({
    pickupDate: '',
    pickupTime: '',
    returnDate: '',
    returnTime: '',
    bypassCalendarCheck: false
  });
  const [savingSchedule, setSavingSchedule] = useState(false);
  
  // Return checkpoint modal (Delivered → Returned)
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [returnOrder, setReturnOrder] = useState(null);
  const [actualReturnDate, setActualReturnDate] = useState('');
  const [actualReturnTime, setActualReturnTime] = useState('');
  const [lateFeesData, setLateFeesData] = useState(null);
  const [lateFeeAmount, setLateFeeAmount] = useState(0);
  const [processingReturn, setProcessingReturn] = useState(false);

  // Fetch orders
  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true);
      const params = statusFilter !== 'all' ? { status: statusFilter } : {};
      const response = await axios.get(`${API}/rentals/orders`, { params });
      setOrders(response.data.orders || []);
    } catch (error) {
      console.error('Error fetching orders:', error);
      toast.error('Failed to load rental orders');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);
  
  // Fetch settings
  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/rentals/settings`);
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };
  
  // Fetch inventory
  const fetchInventory = async (checkDate = null) => {
    try {
      setLoadingInventory(true);
      const params = checkDate ? { check_date: checkDate } : {};
      const response = await axios.get(`${API}/rentals/inventory`, { params });
      setInventory(response.data.inventory || []);
    } catch (error) {
      console.error('Error fetching inventory:', error);
      toast.error('Failed to load inventory');
    } finally {
      setLoadingInventory(false);
    }
  };
  
  // Save inventory item
  const saveInventoryItem = async (sku, totalQuantity, notes, noReturn, unlimitedInventory, maxPerOrder, replacementCost, flatRate) => {
    setSavingInventory(true);
    try {
      await axios.put(`${API}/rentals/inventory/${sku}`, {
        total_quantity: totalQuantity,
        notes: notes,
        no_return: noReturn,
        unlimited_inventory: unlimitedInventory,
        max_per_order: maxPerOrder,
        replacement_cost: replacementCost,
        flat_rate: flatRate
      });
      toast.success('Inventory updated');
      setEditingInventory(null);
      fetchInventory();
    } catch (error) {
      toast.error('Failed to update inventory');
    } finally {
      setSavingInventory(false);
    }
  };
  
  // Upload image for rental item
  const uploadRentalImage = async (sku, file) => {
    if (!file) return;
    
    // Save scroll position before upload - target the ScrollArea viewport
    const scrollContainer = document.querySelector('[data-inventory-scroll] [data-radix-scroll-area-viewport]');
    const scrollTop = scrollContainer?.scrollTop || 0;
    
    setUploadingImage(sku);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/rentals/inventory/${sku}/image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('Image uploaded successfully');
      
      // Update inventory item locally instead of full refetch to preserve scroll
      setInventory(prev => prev.map(item => 
        item.sku === sku ? { ...item, image_url: response.data.image_url } : item
      ));
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload image');
    } finally {
      setUploadingImage(null);
    }
  };
  
  // Delete image for rental item
  const deleteRentalImage = async (sku) => {
    if (!confirm('Remove this image?')) return;
    
    try {
      await axios.delete(`${API}/rentals/inventory/${sku}/image`);
      toast.success('Image removed');
      
      // Update inventory item locally instead of full refetch to preserve scroll
      setInventory(prev => prev.map(item => 
        item.sku === sku ? { ...item, image_url: null } : item
      ));
      
    } catch (error) {
      toast.error('Failed to remove image');
    }
  };
  
  // Handle file input change
  const handleImageSelect = (sku, event) => {
    const file = event.target.files?.[0];
    if (file) {
      uploadRentalImage(sku, file);
    }
    // Reset the input so the same file can be selected again
    event.target.value = '';
  };
  
  // Create rental product
  const createRentalProduct = async () => {
    if (!newProduct.name || !newProduct.price) {
      toast.error('Name and price are required');
      return;
    }
    
    setSavingProduct(true);
    try {
      await axios.post(`${API}/rentals/products`, {
        name: newProduct.name,
        price: parseFloat(newProduct.price),
        description: newProduct.description,
        sku: newProduct.sku || null
      });
      toast.success('Rental product created');
      setShowAddProduct(false);
      setNewProduct({ name: '', price: '', description: '', sku: '' });
      fetchInventory();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create product');
    } finally {
      setSavingProduct(false);
    }
  };
  
  // Delete rental product
  const deleteRentalProduct = async (sku) => {
    if (!confirm('Are you sure you want to delete this rental product?')) return;
    
    try {
      await axios.delete(`${API}/rentals/products/${sku}`);
      toast.success('Product deleted');
      fetchInventory();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete product');
    }
  };
  
  // Fetch products and instructors for manual order
  const fetchProductsAndInstructors = async () => {
    try {
      const [productsRes, instructorsRes] = await Promise.all([
        axios.get(`${API}/rentals/products`),
        axios.get(`${API}/rentals/eligible-instructors`)
      ]);
      // Combine rentals and ecards for manual order selection
      const rentals = productsRes.data.rentals || [];
      const ecards = (productsRes.data.ecards || []).map(e => ({ ...e, type: 'ecard' }));
      setAvailableProducts([...rentals, ...ecards]);
      setAvailableInstructors(instructorsRes.data.instructors || []);
    } catch (error) {
      console.error('Error fetching products/instructors:', error);
    }
  };
  
  // Open manual order dialog
  const openManualOrderDialog = () => {
    fetchProductsAndInstructors();
    setManualOrder({
      customerName: '',
      customerEmail: '',
      customerPhone: '',
      instructorId: '',
      pickupDate: '',
      pickupTime: '09:00',
      returnDate: '',
      returnTime: '17:00',
      pickupLocation: settings?.pickup_locations?.[0]?.id || '',
      items: [],
      notes: '',
      paymentMethod: 'cash',
      depositCollected: true,
      depositExempt: false,
      customDeposit: 0
    });
    setShowManualOrder(true);
  };
  
  // Add item to manual order
  const addItemToManualOrder = (product) => {
    const existing = manualOrder.items.find(i => i.sku === product.sku);
    const minOrder = product.min_order || 1;
    
    if (existing) {
      setManualOrder(prev => ({
        ...prev,
        items: prev.items.map(i => 
          i.sku === product.sku ? { ...i, quantity: i.quantity + 1 } : i
        )
      }));
    } else {
      // For eCards, start with minimum order quantity
      const initialQty = product.type === 'ecard' ? minOrder : 1;
      setManualOrder(prev => ({
        ...prev,
        items: [...prev.items, {
          sku: product.sku,
          name: product.name,
          quantity: initialQty,
          unit_price: product.price,
          type: product.type || 'rental',
          flat_rate: product.flat_rate || false,
          min_order: minOrder
        }]
      }));
      
      if (product.type === 'ecard' && minOrder > 1) {
        toast.info(`Minimum order for ${product.name} is ${minOrder}`);
      }
    }
  };
  
  // Update manual order item quantity
  const updateManualOrderItem = (sku, quantity) => {
    const item = manualOrder.items.find(i => i.sku === sku);
    const minOrder = item?.min_order || 1;
    
    // For eCards, enforce minimum
    if (item?.type === 'ecard' && quantity < minOrder && quantity > 0) {
      toast.error(`Minimum order for ${item.name} is ${minOrder}`);
      return;
    }
    
    if (quantity <= 0) {
      setManualOrder(prev => ({
        ...prev,
        items: prev.items.filter(i => i.sku !== sku)
      }));
    } else {
      setManualOrder(prev => ({
        ...prev,
        items: prev.items.map(i => 
          i.sku === sku ? { ...i, quantity } : i
        )
      }));
    }
  };
  
  // Calculate manual order total
  const calculateManualOrderTotal = () => {
    if (!manualOrder.pickupDate || !manualOrder.returnDate) return { subtotal: 0, deposit: 0, total: 0, days: 0 };
    
    // Calculate days based on actual hours (same as customer-facing page)
    const pickup = new Date(`${manualOrder.pickupDate}T${manualOrder.pickupTime || '09:00'}`);
    const returnDt = new Date(`${manualOrder.returnDate}T${manualOrder.returnTime || '17:00'}`);
    
    const diffMs = returnDt - pickup;
    const diffHours = diffMs / (1000 * 60 * 60);
    
    // If 24 hours or less, it's 1 day. Otherwise, round up to nearest day.
    const days = diffHours <= 24 ? 1 : Math.ceil(diffHours / 24);
    
    // Calculate subtotal - flat_rate items and eCards charge once, regular items charge per day
    const subtotal = manualOrder.items.reduce((sum, item) => {
      // eCards and flat_rate items are charged once, not per day
      const itemDays = (item.flat_rate || item.type === 'ecard') ? 1 : days;
      return sum + (item.unit_price * item.quantity * itemDays);
    }, 0);
    // Use custom deposit amount (defaults to 0)
    const deposit = parseFloat(manualOrder.customDeposit) || 0;
    
    // Get delivery fee based on selected pickup location
    // If custom location, use default delivery fee; otherwise use location-specific fee
    let deliveryFee = settings?.delivery_fee || 0;
    if (manualOrder.pickupLocation && manualOrder.pickupLocation !== 'custom') {
      const selectedLocation = settings?.pickup_locations?.find(l => l.id === manualOrder.pickupLocation);
      if (selectedLocation) {
        deliveryFee = selectedLocation.delivery_fee || 0;
      }
    }
    
    return { subtotal, deposit, deliveryFee, total: subtotal + deposit + deliveryFee, days };
  };
  
  // Create manual rental order
  const createManualOrder = async () => {
    if (!manualOrder.customerName || !manualOrder.customerEmail || !manualOrder.instructorId) {
      toast.error('Please fill in customer name, email, and select an instructor');
      return;
    }
    if (!manualOrder.pickupDate || !manualOrder.returnDate) {
      toast.error('Please select pickup and return dates');
      return;
    }
    if (manualOrder.items.length === 0) {
      toast.error('Please add at least one item');
      return;
    }
    
    setSavingManualOrder(true);
    try {
      const totals = calculateManualOrderTotal();
      const instructor = availableInstructors.find(i => i.id === manualOrder.instructorId);
      
      const orderData = {
        customer: {
          instructor_id: manualOrder.instructorId,
          name: manualOrder.customerName,
          email: manualOrder.customerEmail,
          phone: manualOrder.customerPhone || ''
        },
        items: manualOrder.items.map(item => ({
          sku: item.sku,
          name: item.name,
          quantity: item.quantity,
          unit_price: item.unit_price,
          days: item.flat_rate ? 1 : totals.days,
          type: item.type || 'rental',
          flat_rate: item.flat_rate || false
        })),
        pickup_location: {
          type: 'predefined',
          predefined_id: manualOrder.pickupLocation,
          predefined_name: settings?.pickup_locations?.find(l => l.id === manualOrder.pickupLocation)?.name || manualOrder.pickupLocation,
          custom_address: null
        },
        // Dropoff location is same as pickup location
        dropoff_location: {
          type: 'predefined',
          predefined_id: manualOrder.pickupLocation,
          predefined_name: settings?.pickup_locations?.find(l => l.id === manualOrder.pickupLocation)?.name || manualOrder.pickupLocation,
          custom_address: null
        },
        pickup_datetime: `${manualOrder.pickupDate}T${manualOrder.pickupTime}`,
        return_datetime: `${manualOrder.returnDate}T${manualOrder.returnTime}`,
        deposit_amount: totals.deposit,
        delivery_fee: totals.deliveryFee,
        subtotal: totals.subtotal,
        total: totals.total,
        terms_accepted: true,
        notes: `[MANUAL ORDER - ${manualOrder.paymentMethod.toUpperCase()}] ${manualOrder.notes}`,
        // Manual order specific fields
        is_manual_order: true,
        payment_method: manualOrder.paymentMethod,
        deposit_exempt: totals.deposit === 0
      };
      
      const response = await axios.post(`${API}/rentals/orders`, orderData);
      
      toast.success(`Manual order created: ${response.data.order_id}`);
      setShowManualOrder(false);
      fetchOrders();
    } catch (error) {
      console.error('Error creating manual order:', error);
      // Handle Pydantic validation errors (422)
      const detail = error.response?.data?.detail;
      if (Array.isArray(detail)) {
        // Pydantic returns array of validation errors
        const messages = detail.map(err => `${err.loc?.join('.')}: ${err.msg}`).join(', ');
        toast.error(`Validation error: ${messages}`);
      } else if (typeof detail === 'string') {
        toast.error(detail);
      } else {
        toast.error('Failed to create order. Please check all fields.');
      }
    } finally {
      setSavingManualOrder(false);
    }
  };
  
  useEffect(() => {
    if (open) {
      fetchOrders();
      fetchSettings();
      fetchInventory();
    }
  }, [open, fetchOrders]);
  
  // Close image menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (imageMenuOpen && !event.target.closest('[data-image-menu]')) {
        setImageMenuOpen(null);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [imageMenuOpen]);
  
  // View order detail
  const viewOrder = async (orderId) => {
    try {
      const response = await axios.get(`${API}/rentals/orders/${orderId}`);
      setSelectedOrder(response.data);
      setShowOrderDetail(true);
    } catch (error) {
      toast.error('Failed to load order details');
    }
  };
  
  // Update status
  const handleStatusUpdate = async () => {
    if (!newStatus || !selectedOrder) return;
    
    setProcessingStatus(true);
    try {
      await axios.put(`${API}/rentals/orders/${selectedOrder.order_id}/status`, {
        status: newStatus,
        notes: statusNotes
      });
      
      toast.success('Status updated successfully');
      setShowStatusUpdate(false);
      setStatusNotes('');
      
      // Refresh order
      const response = await axios.get(`${API}/rentals/orders/${selectedOrder.order_id}`);
      setSelectedOrder(response.data);
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    } finally {
      setProcessingStatus(false);
    }
  };
  
  // Process refund
  const handleRefund = async () => {
    if (!selectedOrder) return;
    
    setProcessingRefund(true);
    try {
      await axios.post(`${API}/rentals/orders/${selectedOrder.order_id}/refund`, {
        refund_type: refundType,
        refund_amount: parseFloat(refundAmount),
        notes: refundNotes
      });
      
      toast.success('Refund processed successfully');
      setShowRefundDialog(false);
      
      // Refresh order
      const response = await axios.get(`${API}/rentals/orders/${selectedOrder.order_id}`);
      setSelectedOrder(response.data);
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process refund');
    } finally {
      setProcessingRefund(false);
    }
  };
  
  // Save items
  const handleSaveItems = async () => {
    if (!selectedOrder) return;
    
    setSavingItems(true);
    try {
      await axios.put(`${API}/rentals/orders/${selectedOrder.order_id}/items`, tempItems);
      
      toast.success('Items updated successfully');
      setEditingItems(false);
      
      // Refresh order
      const response = await axios.get(`${API}/rentals/orders/${selectedOrder.order_id}`);
      setSelectedOrder(response.data);
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update items');
    } finally {
      setSavingItems(false);
    }
  };
  
  // Open edit schedule dialog
  const openEditScheduleDialog = () => {
    if (!selectedOrder) return;
    
    // Parse existing pickup/return datetimes
    const pickupDt = selectedOrder.pickup_datetime ? new Date(selectedOrder.pickup_datetime) : null;
    const returnDt = selectedOrder.return_datetime ? new Date(selectedOrder.return_datetime) : null;
    
    setEditScheduleData({
      pickupDate: pickupDt ? pickupDt.toISOString().split('T')[0] : '',
      pickupTime: pickupDt ? pickupDt.toTimeString().slice(0, 5) : '09:00',
      returnDate: returnDt ? returnDt.toISOString().split('T')[0] : '',
      returnTime: returnDt ? returnDt.toTimeString().slice(0, 5) : '17:00',
      bypassCalendarCheck: false
    });
    setShowEditSchedule(true);
  };
  
  // Save schedule changes
  const handleSaveSchedule = async () => {
    if (!selectedOrder) return;
    
    setSavingSchedule(true);
    try {
      const updateData = {
        bypass_calendar_check: editScheduleData.bypassCalendarCheck
      };
      
      // Build pickup datetime if both date and time are set
      if (editScheduleData.pickupDate && editScheduleData.pickupTime) {
        updateData.pickup_datetime = `${editScheduleData.pickupDate}T${editScheduleData.pickupTime}`;
      }
      
      // Build return datetime if both date and time are set
      if (editScheduleData.returnDate && editScheduleData.returnTime) {
        updateData.return_datetime = `${editScheduleData.returnDate}T${editScheduleData.returnTime}`;
      }
      
      const response = await axios.put(
        `${API}/rentals/orders/${selectedOrder.order_id}/schedule`,
        updateData
      );
      
      toast.success(response.data.message || 'Schedule updated successfully');
      setShowEditSchedule(false);
      
      // Refresh order
      const orderResponse = await axios.get(`${API}/rentals/orders/${selectedOrder.order_id}`);
      setSelectedOrder(orderResponse.data);
      fetchOrders();
    } catch (error) {
      const detail = error.response?.data?.detail || 'Failed to update schedule';
      // Check if it's a calendar conflict error
      if (detail.includes('conflicts with')) {
        toast.error(
          <div>
            <p className="font-medium">Schedule Conflict</p>
            <p className="text-sm mt-1">{detail}</p>
            <p className="text-xs mt-2 text-amber-300">Enable "Override Calendar" to force this time.</p>
          </div>,
          { duration: 8000 }
        );
      } else {
        toast.error(detail);
      }
    } finally {
      setSavingSchedule(false);
    }
  };
  
  // Save settings
  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      // Collect location data from refs before saving
      const updatedLocations = editingSettings.pickup_locations?.map((loc, idx) => {
        const refs = locationRefsRef.current[idx];
        if (refs) {
          return {
            id: refs.getId(),
            name: refs.getName(),
            address: refs.getAddress(),
            delivery_fee: refs.getFee()
          };
        }
        return loc;
      }) || [];
      
      const settingsToSave = {
        ...editingSettings,
        pickup_locations: updatedLocations
      };
      
      await axios.put(`${API}/rentals/settings`, settingsToSave);
      toast.success('Settings saved');
      setSettings(settingsToSave);
      setEditingSettings(null);
      setShowSettings(false);
      locationRefsRef.current = {}; // Clear refs
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSavingSettings(false);
    }
  };
  
  // Quick status advance handler
  const openQuickStatusModal = (order, e) => {
    e.stopPropagation(); // Prevent order selection
    const nextStatus = getNextStatus(order.status);
    if (!nextStatus) {
      toast.error('Order is already complete or cancelled');
      return;
    }
    
    // Route to appropriate checkpoint modal based on transition
    if (nextStatus === 'ready_for_pickup') {
      // Processing → Ready: Fulfillment checkpoint
      openFulfillmentModal(order);
      return;
    }
    
    if (nextStatus === 'delivered') {
      // Ready → Delivered: Delivery/pickup confirmation with signature
      openDeliveryModal(order);
      return;
    }
    
    if (nextStatus === 'returned') {
      // Delivered → Returned: Return checkpoint with late fee calculation
      openReturnModal(order);
      return;
    }
    
    if (nextStatus === 'inspected') {
      // Returned → Inspected: Inspection checkpoint (existing)
      openInspectionModal(order);
      return;
    }
    
    // For other transitions, use the quick status modal
    setQuickStatusOrder({ ...order, nextStatus });
    setQuickStatusNotes('');
    setShowQuickStatusModal(true);
  };
  
  // ==========================================
  // FULFILLMENT CHECKPOINT (Processing → Ready)
  // ==========================================
  const openFulfillmentModal = async (order) => {
    setFulfillmentOrder(order);
    // Initialize fulfillment items with ordered quantities
    const items = (order.items || [])
      .filter(item => item.type !== 'ecard') // Exclude eCards
      .map(item => ({
        sku: item.sku,
        name: item.name,
        unit_price: parseFloat(item.unit_price) || parseFloat(item.price) || 0,
        ordered_quantity: item.quantity || 1,
        filled_quantity: item.quantity || 1, // Default to full fill
        original_quantity: item.quantity || 1 // Keep track of original
      }));
    
    setFulfillmentItems(items);
    setFulfillmentNote('');
    setReplacementItems([]);
    setShowFulfillmentModal(true);
    
    // Fetch available rental products for replacements
    try {
      const response = await axios.get(`${API}/rentals/products`);
      // API returns {rentals: [...], ecards: [...]}
      setAvailableReplacements(response.data?.rentals || []);
    } catch (error) {
      console.error('Error fetching rental products:', error);
      setAvailableReplacements([]);
    }
  };
  
  const updateFulfillmentItem = (index, value) => {
    setFulfillmentItems(prev => {
      const updated = [...prev];
      const qty = value === '' ? '' : Math.max(0, Math.min(parseInt(value) || 0, updated[index].ordered_quantity));
      updated[index] = { ...updated[index], filled_quantity: qty };
      return updated;
    });
  };
  
  // Open replacement picker for a specific item
  const openReplacementPicker = (sku) => {
    setReplacementForSku(sku);
    setShowReplacementPicker(true);
  };
  
  // Add a replacement item
  const addReplacementItem = (product) => {
    if (!replacementForSku) return;
    
    // Check if we already have a replacement for this original item with this product
    const existingIndex = replacementItems.findIndex(
      r => r.original_sku === replacementForSku && r.replacement_sku === product.sku
    );
    
    if (existingIndex >= 0) {
      // Increment quantity
      setReplacementItems(prev => {
        const updated = [...prev];
        updated[existingIndex].quantity += 1;
        return updated;
      });
    } else {
      // Add new replacement
      setReplacementItems(prev => [...prev, {
        original_sku: replacementForSku,
        replacement_sku: product.sku,
        replacement_name: product.name,
        replacement_price: parseFloat(product.price) || 0,
        quantity: 1
      }]);
    }
    
    setShowReplacementPicker(false);
    setReplacementForSku(null);
  };
  
  // Remove a replacement item
  const removeReplacementItem = (index) => {
    setReplacementItems(prev => prev.filter((_, i) => i !== index));
  };
  
  // Update replacement quantity
  const updateReplacementQuantity = (index, value) => {
    const qty = value === '' ? '' : Math.max(1, parseInt(value) || 1);
    setReplacementItems(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], quantity: qty };
      return updated;
    });
  };
  
  const calculateFulfillmentRefund = () => {
    if (!fulfillmentOrder) return { hasShortage: false, refundAmount: 0, replacementCost: 0, netRefund: 0, shortages: [], days: 1 };
    
    // Calculate rental days - handle both datetime and date formats
    let pickup, returnD;
    try {
      pickup = new Date(fulfillmentOrder.pickup_datetime || fulfillmentOrder.pickup_date);
      returnD = new Date(fulfillmentOrder.return_datetime || fulfillmentOrder.return_date);
    } catch (e) {
      pickup = new Date();
      returnD = new Date();
    }
    
    const days = Math.max(1, Math.ceil((returnD - pickup) / (1000 * 60 * 60 * 24)));
    
    const shortages = fulfillmentItems
      .filter(item => (parseInt(item.filled_quantity) || 0) < item.ordered_quantity)
      .map(item => {
        const shortage = item.ordered_quantity - (parseInt(item.filled_quantity) || 0);
        const unitPrice = parseFloat(item.unit_price) || 0;
        const refund = shortage * unitPrice * days;
        return { ...item, shortage, refund: isNaN(refund) ? 0 : refund };
      });
    
    const totalRefund = shortages.reduce((sum, item) => sum + (item.refund || 0), 0);
    
    // Calculate replacement cost
    const replacementCost = replacementItems.reduce((sum, item) => {
      const cost = (parseInt(item.quantity) || 0) * (parseFloat(item.replacement_price) || 0) * days;
      return sum + cost;
    }, 0);
    
    // Net refund = original refund - replacement cost (minimum 0)
    const netRefund = Math.max(0, totalRefund - replacementCost);
    // Additional charge if replacement costs more
    const additionalCharge = Math.max(0, replacementCost - totalRefund);
    
    return { 
      hasShortage: shortages.length > 0, 
      refundAmount: isNaN(totalRefund) ? 0 : totalRefund, 
      replacementCost: isNaN(replacementCost) ? 0 : replacementCost,
      netRefund: isNaN(netRefund) ? 0 : netRefund,
      additionalCharge: isNaN(additionalCharge) ? 0 : additionalCharge,
      shortages, 
      days 
    };
  };
  
  const handleFulfillmentSubmit = async (holdCredit = true) => {
    if (!fulfillmentOrder) return;
    
    const { hasShortage, netRefund, additionalCharge, shortages, days } = calculateFulfillmentRefund();
    
    // Validate: if there's a shortage, require a note
    if (hasShortage && !fulfillmentNote.trim()) {
      toast.error('Please provide a note explaining why the order could not be fully filled');
      return;
    }
    
    setProcessingFulfillment(true);
    try {
      // Build the fulfillment data with replacement items
      // Note: We NO LONGER process refund here - it's held for final deposit refund
      const fulfillmentData = {
        items: fulfillmentItems.map(item => ({
          sku: item.sku,
          ordered_quantity: item.ordered_quantity,
          filled_quantity: parseInt(item.filled_quantity) || 0
        })),
        replacement_items: replacementItems.map(item => ({
          original_sku: item.original_sku,
          replacement_sku: item.replacement_sku,
          replacement_name: item.replacement_name,
          replacement_price: item.replacement_price,
          quantity: parseInt(item.quantity) || 0
        })),
        note: fulfillmentNote,
        hold_credit: holdCredit && hasShortage && netRefund > 0,  // Hold credit for final refund
        credit_amount: holdCredit && hasShortage ? netRefund : 0,  // Amount to add to deposit refund
        additional_charge: additionalCharge
      };
      
      // Call the fulfillment endpoint
      const response = await axios.post(`${API}/rentals/orders/${fulfillmentOrder.order_id}/fulfill`, fulfillmentData);
      
      if (response.data.credit_held && netRefund > 0) {
        toast.success(`Order marked ready. $${netRefund.toFixed(2)} credit held for final refund.`);
      } else if (additionalCharge > 0) {
        toast.success(`Order marked ready. Additional charge of $${additionalCharge.toFixed(2)} for replacements.`);
      } else if (hasShortage && !holdCredit) {
        toast.success('Order marked ready without credit.');
      } else {
        toast.success('Order marked ready for pickup!');
      }
      
      fetchOrders();
      setShowFulfillmentModal(false);
      setFulfillmentOrder(null);
      setFulfillmentItems([]);
      setFulfillmentNote('');
      setReplacementItems([]);
      
      // Update selected order if viewing
      if (selectedOrder?.order_id === fulfillmentOrder.order_id) {
        const updatedOrder = await axios.get(`${API}/rentals/orders/${fulfillmentOrder.order_id}`);
        setSelectedOrder(updatedOrder.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process fulfillment');
    } finally {
      setProcessingFulfillment(false);
    }
  };
  
  // ==========================================
  // DELIVERY CHECKPOINT (Ready → Delivered)
  // ==========================================
  const openDeliveryModal = (order) => {
    setDeliveryOrder(order);
    setDeliverySignature(null);
    setDeliveryAgreed(false);
    setDeliveryConditionNotes('');
    // Initialize item conditions
    const conditions = {};
    (order.items || []).forEach(item => {
      conditions[item.sku] = item.condition_out || '';
    });
    setDeliveryItemConditions(conditions);
    setShowDeliveryModal(true);
  };
  
  // Signature canvas functions
  const startDrawing = (e) => {
    e.preventDefault();
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;
    
    const rect = canvas.getBoundingClientRect();
    // Scale coordinates to account for canvas size vs display size
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    const clientX = e.clientX ?? e.touches?.[0]?.clientX;
    const clientY = e.clientY ?? e.touches?.[0]?.clientY;
    
    const x = (clientX - rect.left) * scaleX;
    const y = (clientY - rect.top) * scaleY;
    
    const ctx = canvas.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };
  
  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;
    
    const rect = canvas.getBoundingClientRect();
    // Scale coordinates to account for canvas size vs display size
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    const clientX = e.clientX ?? e.touches?.[0]?.clientX;
    const clientY = e.clientY ?? e.touches?.[0]?.clientY;
    
    const x = (clientX - rect.left) * scaleX;
    const y = (clientY - rect.top) * scaleY;
    
    const ctx = canvas.getContext('2d');
    ctx.lineTo(x, y);
    ctx.strokeStyle = '#14b8a6';
    ctx.lineWidth = 3;  // Increased line width for better visibility
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';  // Smoother joins
    ctx.stroke();
  };
  
  const stopDrawing = () => {
    setIsDrawing(false);
  };
  
  const clearSignature = () => {
    const canvas = signatureCanvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      setDeliverySignature(null);
    }
  };
  
  // Open full-screen signature modal
  const openSignatureModal = () => {
    setShowSignatureModal(true);
    // Clear any existing signature when opening
    setTimeout(() => {
      const canvas = signatureCanvasRef.current;
      if (canvas) {
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        // Set white background
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }
    }, 100);
  };
  
  // Save signature and close modal
  const saveSignature = () => {
    const canvas = signatureCanvasRef.current;
    if (canvas) {
      const dataUrl = canvas.toDataURL();
      // Check if canvas has any drawing (not just white background)
      const ctx = canvas.getContext('2d');
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const hasDrawing = imageData.data.some((pixel, i) => {
        // Check if any pixel is not white (R=255, G=255, B=255)
        if (i % 4 === 3) return false; // Skip alpha channel
        const idx = Math.floor(i / 4) * 4;
        return !(imageData.data[idx] === 255 && imageData.data[idx+1] === 255 && imageData.data[idx+2] === 255);
      });
      
      if (hasDrawing) {
        setDeliverySignature(dataUrl);
        setShowSignatureModal(false);
      } else {
        toast.error('Please sign before saving');
      }
    }
  };
  
  const handleDeliverySubmit = async () => {
    if (!deliveryOrder) return;
    
    if (!deliveryAgreed) {
      toast.error('Customer must agree to the rental terms');
      return;
    }
    
    if (!deliverySignature) {
      toast.error('Customer signature is required');
      return;
    }
    
    setProcessingDelivery(true);
    try {
      // Get current filled quantities from the order with condition notes
      const deliveryData = {
        signature: deliverySignature,
        agreed_to_terms: true,
        condition_notes: deliveryConditionNotes,
        items: (deliveryOrder.items || []).map(item => ({
          sku: item.sku,
          delivered_quantity: item.filled_quantity || item.quantity,
          condition_out: deliveryItemConditions[item.sku] || ''
        }))
      };
      
      await axios.post(`${API}/rentals/orders/${deliveryOrder.order_id}/deliver`, deliveryData);
      
      toast.success('Equipment delivered! Customer signature recorded.');
      
      fetchOrders();
      setShowDeliveryModal(false);
      setDeliveryOrder(null);
      setDeliverySignature(null);
      setDeliveryAgreed(false);
      setDeliveryConditionNotes('');
      setDeliveryItemConditions({});
      
      // Update selected order if viewing
      if (selectedOrder?.order_id === deliveryOrder.order_id) {
        const updatedOrder = await axios.get(`${API}/rentals/orders/${deliveryOrder.order_id}`);
        setSelectedOrder(updatedOrder.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process delivery');
    } finally {
      setProcessingDelivery(false);
    }
  };

  // ==========================================
  // RETURN CHECKPOINT (Delivered → Returned)
  // ==========================================
  
  // Get current time in PST formatted for input fields
  const getCurrentPSTTime = () => {
    const now = new Date();
    // Convert to PST
    const pstOptions = { timeZone: 'America/Los_Angeles' };
    const pstDate = new Date(now.toLocaleString('en-US', pstOptions));
    
    const year = pstDate.getFullYear();
    const month = String(pstDate.getMonth() + 1).padStart(2, '0');
    const day = String(pstDate.getDate()).padStart(2, '0');
    const hours = String(pstDate.getHours()).padStart(2, '0');
    const minutes = String(pstDate.getMinutes()).padStart(2, '0');
    
    return {
      date: `${year}-${month}-${day}`,
      time: `${hours}:${minutes}`
    };
  };
  
  // Calculate late fees based on actual return vs scheduled return
  const calculateLateFees = (order, actualDate, actualTime) => {
    if (!order || !actualDate || !actualTime) return null;
    
    const scheduledReturn = new Date(order.return_datetime);
    const actualReturn = new Date(`${actualDate}T${actualTime}`);
    
    // Calculate difference in milliseconds
    const diffMs = actualReturn - scheduledReturn;
    const diffMinutes = diffMs / (1000 * 60);
    
    // If on time or early, or within 30 minutes grace period
    if (diffMinutes <= 30) {
      return {
        isLate: false,
        diffMinutes: Math.max(0, diffMinutes),
        extraDays: 0,
        dailyRate: 0,
        calculatedFee: 0,
        scheduledReturn: scheduledReturn.toLocaleString('en-US', { timeZone: 'America/Los_Angeles' }),
        actualReturn: actualReturn.toLocaleString('en-US', { timeZone: 'America/Los_Angeles' })
      };
    }
    
    // Calculate extra days (30 min - 24 hrs = 1 day, 24-48 hrs = 2 days, etc.)
    const diffHours = diffMinutes / 60;
    const extraDays = Math.ceil((diffMinutes - 30) / (24 * 60)); // Subtract 30 min grace, then round up to days
    
    // Calculate daily rate from order items
    const rentalItems = (order.items || []).filter(i => i.type !== 'ecard');
    const totalDailyRate = rentalItems.reduce((sum, item) => {
      const itemRate = parseFloat(item.unit_price) || 0;
      const qty = parseInt(item.quantity) || 1;
      return sum + (itemRate * qty);
    }, 0);
    
    const calculatedFee = extraDays * totalDailyRate;
    
    return {
      isLate: true,
      diffMinutes: Math.round(diffMinutes),
      diffHours: diffHours.toFixed(1),
      extraDays,
      dailyRate: totalDailyRate,
      calculatedFee,
      scheduledReturn: scheduledReturn.toLocaleString('en-US', { timeZone: 'America/Los_Angeles' }),
      actualReturn: actualReturn.toLocaleString('en-US', { timeZone: 'America/Los_Angeles' })
    };
  };
  
  const openReturnModal = (order) => {
    setReturnOrder(order);
    
    // Default to current PST time
    const currentPST = getCurrentPSTTime();
    setActualReturnDate(currentPST.date);
    setActualReturnTime(currentPST.time);
    
    // Calculate initial late fees
    const fees = calculateLateFees(order, currentPST.date, currentPST.time);
    setLateFeesData(fees);
    setLateFeeAmount(fees?.calculatedFee || 0);
    
    setShowReturnModal(true);
  };
  
  // Update late fees when date/time changes
  const handleReturnDateTimeChange = (date, time) => {
    if (date) setActualReturnDate(date);
    if (time) setActualReturnTime(time);
    
    const newDate = date || actualReturnDate;
    const newTime = time || actualReturnTime;
    
    if (newDate && newTime && returnOrder) {
      const fees = calculateLateFees(returnOrder, newDate, newTime);
      setLateFeesData(fees);
      setLateFeeAmount(fees?.calculatedFee || 0);
    }
  };
  
  const handleReturnSubmit = async (chargeLateFee) => {
    if (!returnOrder) return;
    
    setProcessingReturn(true);
    try {
      const response = await axios.post(`${API}/rentals/orders/${returnOrder.order_id}/return`, {
        actual_return_datetime: `${actualReturnDate}T${actualReturnTime}`,
        charge_late_fee: chargeLateFee,
        late_fee_amount: chargeLateFee ? lateFeeAmount : 0,
        late_fee_details: chargeLateFee && lateFeesData?.isLate ? {
          scheduled_return: lateFeesData.scheduledReturn,
          actual_return: lateFeesData.actualReturn,
          extra_days: lateFeesData.extraDays,
          daily_rate: lateFeesData.dailyRate,
          calculated_fee: lateFeesData.calculatedFee,
          charged_fee: lateFeeAmount
        } : null
      });
      
      toast.success(response.data.message || 'Return processed successfully');
      setShowReturnModal(false);
      setReturnOrder(null);
      
      // Refresh order
      if (selectedOrder?.order_id === returnOrder.order_id) {
        const orderResponse = await axios.get(`${API}/rentals/orders/${returnOrder.order_id}`);
        setSelectedOrder(orderResponse.data);
      }
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process return');
    } finally {
      setProcessingReturn(false);
    }
  };

  // Inspection modal handler
  const openInspectionModal = (order) => {
    setInspectionOrder(order);
    // Initialize inspection items with rented quantities as defaults
    // Filter out eCards and items marked as "no return" in inventory
    const items = (order.items || [])
      .filter(item => {
        // Exclude eCards from inspection
        if (item.type === 'ecard') return false;
        // Exclude items marked as no_return in inventory
        const invItem = inventory.find(inv => inv.sku === item.sku);
        if (invItem?.no_return) return false;
        return true;
      })
      .map(item => {
        // Get replacement cost from order item (captured at delivery) or inventory
        const invItem = inventory.find(inv => inv.sku === item.sku);
        const replacementCost = item.replacement_cost || invItem?.replacement_cost || 0;
        
        return {
          sku: item.sku,
          name: item.name,
          rented_quantity: item.quantity,
          returned_quantity: item.quantity, // Default to full return
          reason: '',
          damage_charge: 0,
          replacement_cost: replacementCost  // Include for reference
        };
      });
    
    if (items.length === 0) {
      // No physical items to inspect - just advance status
      toast.info('No returnable equipment to inspect. Advancing to inspected status.');
      handleQuickStatusUpdateDirect(order.order_id, 'inspected');
      return;
    }
    
    setInspectionItems(items);
    setInspectionNotes('');
    setInspectorSignature(null);  // Reset inspector signature
    setAdditionalFees([]);  // Reset additional fees
    setShowInspectionModal(true);
  };
  
  // Direct status update (for orders with only eCards)
  const handleQuickStatusUpdateDirect = async (orderId, status) => {
    try {
      await axios.put(`${API}/rentals/orders/${orderId}/status`, {
        status: status,
        notes: 'No physical equipment to inspect - eCards only'
      });
      toast.success(`Status updated to ${STATUS_CONFIG[status]?.label}`);
      fetchOrders();
      if (selectedOrder?.order_id === orderId) {
        const response = await axios.get(`${API}/rentals/orders/${orderId}`);
        setSelectedOrder(response.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  };
  
  const updateInspectionItem = (index, field, value) => {
    setInspectionItems(prev => {
      const updated = [...prev];
      const item = { ...updated[index], [field]: value };
      
      // Auto-fill damage charge when returned quantity is reduced
      if (field === 'returned_quantity') {
        const returnedQty = parseInt(value) || 0;
        const missingQty = item.rented_quantity - returnedQty;
        const replacementCost = item.replacement_cost || 0;
        
        if (missingQty > 0 && replacementCost > 0) {
          // Auto-fill with replacement cost * missing quantity
          item.damage_charge = (missingQty * replacementCost).toFixed(2);
        } else if (missingQty <= 0) {
          // Reset damage charge if all items returned (unless manually set)
          item.damage_charge = 0;
        }
      }
      
      updated[index] = item;
      return updated;
    });
  };
  
  const handleInspectionSubmit = async () => {
    if (!inspectionOrder) return;
    
    // Validate: check if any items have missing quantity without reason
    const incompleteItems = inspectionItems.filter(
      item => item.returned_quantity < item.rented_quantity && !item.reason
    );
    
    if (incompleteItems.length > 0) {
      toast.error('Please provide a reason for all items with missing quantities');
      return;
    }
    
    // Validate: require inspector signature
    if (!inspectorSignature) {
      toast.error('Please provide your inspector signature');
      return;
    }
    
    setProcessingInspection(true);
    try {
      // Filter out empty additional fees
      const validFees = additionalFees.filter(fee => fee.description && fee.amount > 0);
      
      const response = await axios.post(`${API}/rentals/orders/${inspectionOrder.order_id}/inspection`, {
        items: inspectionItems,
        notes: inspectionNotes,
        inspector_signature: inspectorSignature,
        additional_fees: validFees.length > 0 ? validFees : null
      });
      
      const data = response.data.inspection_data;
      
      // Show appropriate toast based on result
      if (data.invoice_id) {
        toast.success(`Order complete. Invoice ${data.invoice_id} created for $${data.additional_charge.toFixed(2)}`);
      } else if (data.total_refund > 0) {
        const refundMethod = data.refund_method === 'stripe' ? 'Stripe' : 'Cash';
        toast.success(`Order complete. ${refundMethod} refund: $${data.total_refund.toFixed(2)}`);
      } else if (data.total_damage_charges > 0 || data.total_additional_fees > 0) {
        toast.success(`Order complete. Charges covered by deposit.`);
      } else {
        toast.success('Order complete. All items returned in good condition. Full deposit refunded.');
      }
      
      setShowInspectionModal(false);
      setInspectionOrder(null);
      setInspectionItems([]);
      setInspectionNotes('');
      setInspectorSignature(null);
      setAdditionalFees([]);
      fetchOrders();
      
      // Refresh selected order if applicable
      if (selectedOrder?.order_id === inspectionOrder.order_id) {
        const orderResponse = await axios.get(`${API}/rentals/orders/${inspectionOrder.order_id}`);
        setSelectedOrder(orderResponse.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process inspection');
    } finally {
      setProcessingInspection(false);
    }
  };
  
  // Inspector signature canvas handlers
  const startDrawingInspectorSig = (e) => {
    e.preventDefault();
    setIsDrawingInspectorSig(true);
    const canvas = inspectorSigCanvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    let clientX, clientY;
    if (e.touches) {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else {
      clientX = e.clientX;
      clientY = e.clientY;
    }
    
    const x = (clientX - rect.left) * scaleX;
    const y = (clientY - rect.top) * scaleY;
    
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const drawInspectorSig = (e) => {
    if (!isDrawingInspectorSig) return;
    e.preventDefault();
    const canvas = inspectorSigCanvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    let clientX, clientY;
    if (e.touches) {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else {
      clientX = e.clientX;
      clientY = e.clientY;
    }
    
    const x = (clientX - rect.left) * scaleX;
    const y = (clientY - rect.top) * scaleY;
    
    ctx.lineWidth = 3;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#000';
    ctx.lineTo(x, y);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const stopDrawingInspectorSig = () => {
    setIsDrawingInspectorSig(false);
  };

  const clearInspectorSignature = () => {
    const canvas = inspectorSigCanvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      setInspectorSignature(null);
    }
  };

  const saveInspectorSignature = () => {
    const canvas = inspectorSigCanvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;
      let hasDrawing = false;
      for (let i = 0; i < data.length; i += 4) {
        if (data[i] < 250 || data[i + 1] < 250 || data[i + 2] < 250) {
          hasDrawing = true;
          break;
        }
      }
      
      if (hasDrawing) {
        setInspectorSignature(canvas.toDataURL('image/png'));
        setShowInspectorSignatureModal(false);
        toast.success('Signature captured');
      } else {
        toast.error('Please sign before continuing');
      }
    }
  };

  // Initialize inspector signature canvas when modal opens
  useEffect(() => {
    if (showInspectorSignatureModal && inspectorSigCanvasRef.current) {
      const canvas = inspectorSigCanvasRef.current;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    }
  }, [showInspectorSignatureModal]);
  
  const handleQuickStatusUpdate = async () => {
    if (!quickStatusOrder || !quickStatusOrder.nextStatus) return;
    
    setProcessingQuickStatus(true);
    try {
      await axios.put(`${API}/rentals/orders/${quickStatusOrder.order_id}/status`, {
        status: quickStatusOrder.nextStatus,
        notes: quickStatusNotes
      });
      
      toast.success(`Status updated to ${STATUS_CONFIG[quickStatusOrder.nextStatus]?.label}`);
      setShowQuickStatusModal(false);
      setQuickStatusOrder(null);
      setQuickStatusNotes('');
      fetchOrders();
      
      // If this was the selected order, refresh it too
      if (selectedOrder?.order_id === quickStatusOrder.order_id) {
        const response = await axios.get(`${API}/rentals/orders/${quickStatusOrder.order_id}`);
        setSelectedOrder(response.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    } finally {
      setProcessingQuickStatus(false);
    }
  };
  
  // Get PDF - opens in new tab (not print dialog)
  const getPDF = async (type) => {
    if (!selectedOrder) return;
    
    // Check status restrictions
    const status = selectedOrder.status;
    if (type === 'checkout' && !['delivered', 'returned', 'complete'].includes(status)) {
      toast.error('Checkout PDF is only available after items are delivered');
      return;
    }
    if (type === 'checkin' && status !== 'complete') {
      toast.error('Check-in PDF is only available after inspection is complete');
      return;
    }
    
    // Open PDF in new tab
    window.open(`${API}/rentals/orders/${selectedOrder.order_id}/pdf/${type}`, '_blank');
  };
  
  // Get history PDF - printable log
  const getHistoryPDF = () => {
    if (!selectedOrder) return;
    window.open(`${API}/rentals/orders/${selectedOrder.order_id}/history-pdf`, '_blank');
  };
  
  // Add note to order
  const [newNoteText, setNewNoteText] = useState('');
  const [newNoteType, setNewNoteType] = useState('admin');
  const [sendNoteEmail, setSendNoteEmail] = useState(false);
  const [orderNotes, setOrderNotes] = useState([]);
  
  const fetchOrderNotes = async (orderId) => {
    try {
      const response = await axios.get(`${API}/rentals/orders/${orderId}/notes`);
      setOrderNotes(response.data.notes || []);
    } catch (error) {
      console.error('Failed to fetch notes:', error);
    }
  };
  
  const addNote = async () => {
    if (!selectedOrder || !newNoteText.trim()) {
      toast.error('Please enter a note');
      return;
    }
    
    try {
      await axios.post(`${API}/rentals/orders/${selectedOrder.order_id}/notes`, {
        note_type: newNoteType,
        note_text: newNoteText,
        send_email: newNoteType === 'customer' && sendNoteEmail
      });
      
      toast.success(newNoteType === 'customer' && sendNoteEmail ? 'Note added and emailed to customer' : 'Note added');
      setNewNoteText('');
      setSendNoteEmail(false);
      fetchOrderNotes(selectedOrder.order_id);
    } catch (error) {
      toast.error('Failed to add note');
    }
  };
  
  // Fetch notes when order is selected
  useEffect(() => {
    if (selectedOrder?.order_id) {
      fetchOrderNotes(selectedOrder.order_id);
    } else {
      setOrderNotes([]);
    }
  }, [selectedOrder?.order_id]);
  
  // Get checklist (legacy - for backward compatibility)
  const getChecklist = async (type) => {
    if (!selectedOrder) return;
    
    try {
      const response = await axios.get(`${API}/rentals/orders/${selectedOrder.order_id}/checklist/${type}`);
      // Open in new window for printing
      const printWindow = window.open('', '_blank');
      printWindow.document.write(response.data.html);
      printWindow.document.close();
      printWindow.print();
    } catch (error) {
      toast.error('Failed to generate checklist');
    }
  };
  
  // Filtered orders - exclude cancelled from 'all', show only cancelled in 'cancelled' tab
  const filteredOrders = orders.filter(order => {
    // First apply search filter
    if (searchQuery) {
      const search = searchQuery.toLowerCase();
      const matchesSearch = (
        order.order_id.toLowerCase().includes(search) ||
        order.customer?.name?.toLowerCase().includes(search) ||
        order.customer?.email?.toLowerCase().includes(search)
      );
      if (!matchesSearch) return false;
    }
    
    // Then apply status filter
    if (statusFilter === 'all') {
      // 'All' tab shows everything EXCEPT cancelled
      return order.status !== 'cancelled';
    } else if (statusFilter === 'cancelled') {
      // 'Cancelled' tab shows only cancelled
      return order.status === 'cancelled';
    } else {
      // Other tabs show their specific status
      return order.status === statusFilter;
    }
  });
  
  // Filtered inventory
  const filteredInventory = inventory.filter(item => {
    if (inventorySearch) {
      const search = inventorySearch.toLowerCase();
      return (
        item.sku.toLowerCase().includes(search) ||
        item.name?.toLowerCase().includes(search)
      );
    }
    return true;
  });
  
  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };
  
  // Start items editing
  const startEditingItems = () => {
    setTempItems([...selectedOrder.items]);
    setEditingItems(true);
  };
  
  // Update temp item
  const updateTempItem = (index, field, value) => {
    setTempItems(items => items.map((item, i) => 
      i === index ? { ...item, [field]: value } : item
    ));
  };
  
  // Remove temp item
  const removeTempItem = (index) => {
    setTempItems(items => items.filter((_, i) => i !== index));
  };
  
  if (!open) return null;
  
  return (
    <>
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-slate-800 border-slate-700 w-[95vw] max-w-6xl max-h-[90vh] p-0">
        <DialogHeader className="p-3 md:p-4 border-b border-slate-700">
          <DialogTitle className="text-white flex flex-col md:flex-row items-start md:items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <Package className="h-5 w-5 text-purple-400" />
              <span className="text-sm md:text-base">Equipment Rental Management</span>
            </div>
            <div className="flex gap-2 w-full md:w-auto">
              <Button
                size="sm"
                variant={mainTab === 'orders' ? 'default' : 'outline'}
                onClick={() => setMainTab('orders')}
                className={`flex-1 md:flex-none ${mainTab === 'orders' ? 'bg-purple-600' : 'border-slate-600 text-slate-300'}`}
              >
                <Package className="h-4 w-4 mr-1" />
                Orders
              </Button>
              <Button
                size="sm"
                variant={mainTab === 'inventory' ? 'default' : 'outline'}
                onClick={() => setMainTab('inventory')}
                className={`flex-1 md:flex-none ${mainTab === 'inventory' ? 'bg-orange-600' : 'border-slate-600 text-slate-300'}`}
              >
                <Boxes className="h-4 w-4 mr-1" />
                Inventory
              </Button>
            </div>
          </DialogTitle>
        </DialogHeader>
        
        {/* Inventory Tab */}
        {mainTab === 'inventory' ? (
          <div className="h-[calc(90vh-100px)] md:h-[calc(90vh-80px)] flex flex-col overflow-hidden">
            {/* Inventory Header */}
            <div className="p-2 md:p-3 border-b border-slate-700">
              <div className="flex flex-col md:flex-row gap-2 items-stretch md:items-center">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="Search inventory..."
                    value={inventorySearch}
                    onChange={(e) => setInventorySearch(e.target.value)}
                    className="pl-10 bg-slate-700 border-slate-600 text-white h-9"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Label className="text-slate-400 text-xs whitespace-nowrap">Check Date:</Label>
                  <Input
                    type="date"
                    value={inventoryCheckDate}
                    onChange={(e) => {
                      setInventoryCheckDate(e.target.value);
                      fetchInventory(e.target.value || null);
                    }}
                    className="flex-1 md:w-36 bg-slate-700 border-slate-600 text-white h-9 text-sm"
                  />
                  {inventoryCheckDate && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setInventoryCheckDate('');
                        fetchInventory(null);
                      }}
                      className="h-9 text-slate-400 hover:text-white"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
                <Button
                  size="sm"
                  onClick={() => setShowAddProduct(true)}
                  className="bg-green-600 hover:bg-green-500"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add Rental
                </Button>
                <Button
                  size="sm"
                  onClick={() => fetchInventory(inventoryCheckDate || null)}
                  className="bg-orange-600 hover:bg-orange-500"
                >
                  <RefreshCw className="h-4 w-4" />
                </Button>
              </div>
              <p className="text-xs text-slate-400 mt-2">
                {inventoryCheckDate 
                  ? `Showing availability for ${new Date(inventoryCheckDate).toLocaleDateString()}. Items reserved from Processing until Complete.`
                  : 'Showing all current reservations. Use date filter to check specific date availability.'}
              </p>
            </div>
            
            {/* Inventory List */}
            <ScrollArea className="flex-1" data-inventory-scroll>
              {loadingInventory ? (
                <div className="flex items-center justify-center h-40">
                  <Loader2 className="h-8 w-8 animate-spin text-orange-400" />
                </div>
              ) : filteredInventory.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-40 text-slate-400">
                  <Boxes className="h-10 w-10 mb-2 opacity-50" />
                  <p>No rental items found</p>
                  <p className="text-xs">Sync POS prices to load rental items</p>
                </div>
              ) : (
                <div className="p-3 space-y-2">
                  {filteredInventory.map(item => {
                    const isEditing = editingInventory?.sku === item.sku;
                    const hasReservations = item.reserved_quantity > 0;
                    
                    return (
                      <Card key={item.sku} className={`p-3 border ${hasReservations ? 'bg-orange-900/20 border-orange-600/30' : 'bg-slate-700/50 border-slate-600/50'}`}>
                        <div className="flex items-start justify-between gap-3">
                          {/* Product Image */}
                          <div className="relative flex-shrink-0">
                            {item.image_url ? (
                              <div className="relative group">
                                <img 
                                  src={item.image_url} 
                                  alt={item.name}
                                  className="w-16 h-16 object-cover rounded-lg border border-slate-600 cursor-pointer"
                                  onClick={() => setImageMenuOpen(imageMenuOpen === item.sku ? null : item.sku)}
                                />
                                {uploadingImage === item.sku && (
                                  <div className="absolute inset-0 bg-black/70 rounded-lg flex items-center justify-center">
                                    <Loader2 className="h-5 w-5 animate-spin text-white" />
                                  </div>
                                )}
                                {/* Image action menu */}
                                {imageMenuOpen === item.sku && (
                                  <div className="absolute top-full left-0 mt-1 z-50 bg-slate-800 border border-slate-600 rounded-lg shadow-xl py-1 min-w-[140px]" data-image-menu>
                                    <label className="flex items-center gap-2 px-3 py-2 hover:bg-slate-700 cursor-pointer text-sm text-white">
                                      <Camera className="h-4 w-4" />
                                      Take Photo
                                      <input
                                        type="file"
                                        accept="image/*"
                                        capture="environment"
                                        className="hidden"
                                        onChange={(e) => {
                                          setImageMenuOpen(null);
                                          handleImageSelect(item.sku, e);
                                        }}
                                      />
                                    </label>
                                    <label className="flex items-center gap-2 px-3 py-2 hover:bg-slate-700 cursor-pointer text-sm text-white">
                                      <Upload className="h-4 w-4" />
                                      Upload File
                                      <input
                                        type="file"
                                        accept="image/*"
                                        className="hidden"
                                        onChange={(e) => {
                                          setImageMenuOpen(null);
                                          handleImageSelect(item.sku, e);
                                        }}
                                      />
                                    </label>
                                    <button 
                                      onClick={() => {
                                        setImageMenuOpen(null);
                                        deleteRentalImage(item.sku);
                                      }}
                                      className="flex items-center gap-2 px-3 py-2 hover:bg-slate-700 cursor-pointer text-sm text-red-400 w-full"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                      Remove
                                    </button>
                                  </div>
                                )}
                              </div>
                            ) : (
                              <div className="relative">
                                <div 
                                  onClick={() => setImageMenuOpen(imageMenuOpen === item.sku ? null : item.sku)}
                                  className={`w-16 h-16 rounded-lg border-2 border-dashed border-slate-500 flex flex-col items-center justify-center hover:border-orange-500 hover:bg-slate-700/50 transition-colors cursor-pointer ${uploadingImage === item.sku ? 'pointer-events-none' : ''}`}
                                >
                                  {uploadingImage === item.sku ? (
                                    <Loader2 className="h-5 w-5 animate-spin text-orange-400" />
                                  ) : (
                                    <>
                                      <Camera className="h-5 w-5 text-slate-400" />
                                      <span className="text-[10px] text-slate-500 mt-0.5">Add Photo</span>
                                    </>
                                  )}
                                </div>
                                {/* Image action menu for placeholder */}
                                {imageMenuOpen === item.sku && (
                                  <div className="absolute top-full left-0 mt-1 z-50 bg-slate-800 border border-slate-600 rounded-lg shadow-xl py-1 min-w-[140px]" data-image-menu>
                                    <label className="flex items-center gap-2 px-3 py-2 hover:bg-slate-700 cursor-pointer text-sm text-white">
                                      <Camera className="h-4 w-4" />
                                      Take Photo
                                      <input
                                        type="file"
                                        accept="image/*"
                                        capture="environment"
                                        className="hidden"
                                        onChange={(e) => {
                                          setImageMenuOpen(null);
                                          handleImageSelect(item.sku, e);
                                        }}
                                      />
                                    </label>
                                    <label className="flex items-center gap-2 px-3 py-2 hover:bg-slate-700 cursor-pointer text-sm text-white">
                                      <Upload className="h-4 w-4" />
                                      Upload File
                                      <input
                                        type="file"
                                        accept="image/*"
                                        className="hidden"
                                        onChange={(e) => {
                                          setImageMenuOpen(null);
                                          handleImageSelect(item.sku, e);
                                        }}
                                      />
                                    </label>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                          
                          <div className="flex-1 min-w-0">
                            <p className="text-white font-medium">{item.name}</p>
                            <p className="text-xs text-slate-400">SKU: {item.sku}</p>
                            <p className="text-xs text-slate-400">${item.price?.toFixed(2)}/day</p>
                          </div>
                          
                          <div className="flex items-center gap-4">
                            {/* Inventory Stats */}
                            <div className="text-right">
                              <div className="flex items-center gap-3">
                                <div className="text-center">
                                  <p className="text-xs text-slate-400">Total</p>
                                  {isEditing ? (
                                    <Input
                                      type="number"
                                      min="0"
                                      value={editingInventory.total_quantity}
                                      onChange={(e) => setEditingInventory({
                                        ...editingInventory,
                                        total_quantity: e.target.value === '' ? '' : parseInt(e.target.value)
                                      })}
                                      onBlur={(e) => {
                                        const val = parseInt(e.target.value);
                                        if (isNaN(val) || val < 0) setEditingInventory(prev => ({...prev, total_quantity: 0}));
                                      }}
                                      className="w-16 h-7 bg-slate-600 border-orange-500 text-white text-center text-sm"
                                    />
                                  ) : (
                                    <p className="text-lg font-bold text-white">{item.total_quantity}</p>
                                  )}
                                </div>
                                <div className="text-center">
                                  <p className="text-xs text-slate-400">Reserved</p>
                                  <p className={`text-lg font-bold ${hasReservations ? 'text-orange-400' : 'text-slate-500'}`}>
                                    {item.reserved_quantity}
                                  </p>
                                </div>
                                <div className="text-center">
                                  <p className="text-xs text-slate-400">Available</p>
                                  {item.unlimited_inventory ? (
                                    <p className="text-lg font-bold text-blue-400">∞</p>
                                  ) : (
                                    <p className={`text-lg font-bold ${item.available_quantity > 0 ? 'text-green-400' : 'text-red-400'}`}>
                                      {item.available_quantity}
                                    </p>
                                  )}
                                </div>
                              </div>
                            </div>
                            
                            {/* Edit Button */}
                            {isEditing ? (
                              <div className="flex gap-1">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => setEditingInventory(null)}
                                  className="h-8 text-slate-400"
                                >
                                  Cancel
                                </Button>
                                <Button
                                  size="sm"
                                  onClick={() => saveInventoryItem(
                                    item.sku, 
                                    editingInventory.total_quantity, 
                                    editingInventory.notes, 
                                    editingInventory.no_return,
                                    editingInventory.unlimited_inventory,
                                    editingInventory.max_per_order,
                                    editingInventory.replacement_cost,
                                    editingInventory.flat_rate
                                  )}
                                  disabled={savingInventory}
                                  className="h-8 bg-green-600 hover:bg-green-500"
                                >
                                  {savingInventory ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
                                </Button>
                              </div>
                            ) : (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setEditingInventory({
                                  sku: item.sku,
                                  total_quantity: item.total_quantity,
                                  notes: item.notes || '',
                                  no_return: item.no_return || false,
                                  unlimited_inventory: item.unlimited_inventory || false,
                                  max_per_order: item.max_per_order || 10,
                                  replacement_cost: item.replacement_cost || 0,
                                  flat_rate: item.flat_rate || false
                                })}
                                className="h-8 border-slate-600 text-slate-300"
                              >
                                <Edit2 className="h-3 w-3 mr-1" />
                                Edit
                              </Button>
                            )}
                          </div>
                        </div>
                        
                        {/* Notes and No Return fields when editing */}
                        {isEditing && (
                          <div className="mt-2 space-y-2">
                            <Input
                              placeholder="Notes (optional)"
                              value={editingInventory.notes}
                              onChange={(e) => setEditingInventory({...editingInventory, notes: e.target.value})}
                              className="bg-slate-600 border-slate-500 text-white text-sm"
                            />
                            
                            {/* Unlimited Inventory Option */}
                            <div className="flex items-center gap-3 p-2 bg-slate-700/50 rounded">
                              <Checkbox
                                id={`unlimited-${item.sku}`}
                                checked={editingInventory.unlimited_inventory}
                                onCheckedChange={(checked) => setEditingInventory({...editingInventory, unlimited_inventory: checked})}
                                className="border-slate-500 data-[state=checked]:bg-blue-600"
                              />
                              <div className="flex-1">
                                <Label htmlFor={`unlimited-${item.sku}`} className="text-slate-300 text-sm cursor-pointer">
                                  Unlimited Inventory
                                </Label>
                                <span className="text-xs text-slate-500 ml-2">(Always available)</span>
                              </div>
                              {editingInventory.unlimited_inventory && (
                                <div className="flex items-center gap-2">
                                  <Label className="text-slate-400 text-xs whitespace-nowrap">Max per Order:</Label>
                                  <Input
                                    type="number"
                                    min="1"
                                    value={editingInventory.max_per_order}
                                    onChange={(e) => setEditingInventory({...editingInventory, max_per_order: parseInt(e.target.value) || 1})}
                                    onBlur={(e) => {
                                      const val = parseInt(e.target.value) || 1;
                                      setEditingInventory({...editingInventory, max_per_order: Math.max(1, val)});
                                    }}
                                    className="w-20 h-7 bg-slate-600 border-slate-500 text-white text-sm"
                                  />
                                </div>
                              )}
                            </div>
                            
                            {/* No Return Option */}
                            <div className="flex items-center gap-2 p-2 bg-slate-700/50 rounded">
                              <Checkbox
                                id={`no-return-${item.sku}`}
                                checked={editingInventory.no_return}
                                onCheckedChange={(checked) => setEditingInventory({...editingInventory, no_return: checked})}
                                className="border-slate-500 data-[state=checked]:bg-amber-600"
                              />
                              <Label htmlFor={`no-return-${item.sku}`} className="text-slate-300 text-sm cursor-pointer">
                                No Return Required
                              </Label>
                              <span className="text-xs text-slate-500">(Won't appear in check-in inspection)</span>
                            </div>
                            
                            {/* Replacement Cost */}
                            <div className="flex items-center gap-3 p-2 bg-slate-700/50 rounded">
                              <Label className="text-slate-300 text-sm whitespace-nowrap">Replacement Cost:</Label>
                              <div className="flex items-center gap-1">
                                <span className="text-slate-400">$</span>
                                <Input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  value={editingInventory.replacement_cost || ''}
                                  onChange={(e) => setEditingInventory({...editingInventory, replacement_cost: e.target.value})}
                                  onBlur={(e) => {
                                    const val = parseFloat(e.target.value) || 0;
                                    setEditingInventory({...editingInventory, replacement_cost: Math.max(0, val)});
                                  }}
                                  placeholder="0.00"
                                  className="w-24 h-7 bg-slate-600 border-slate-500 text-white text-sm"
                                />
                              </div>
                              <span className="text-xs text-slate-500">(Cost if lost/damaged)</span>
                            </div>
                            
                            {/* Flat Rate Option */}
                            <div className="flex items-center gap-2 p-2 bg-green-900/30 border border-green-500/30 rounded">
                              <Checkbox
                                id={`flat-rate-${item.sku}`}
                                checked={editingInventory.flat_rate}
                                onCheckedChange={(checked) => setEditingInventory({...editingInventory, flat_rate: checked})}
                                className="border-green-500 data-[state=checked]:bg-green-600"
                              />
                              <Label htmlFor={`flat-rate-${item.sku}`} className="text-green-300 text-sm cursor-pointer">
                                Flat Rate (One-Time Cost)
                              </Label>
                              <span className="text-xs text-green-200/60">(Charge once, not per day)</span>
                            </div>
                          </div>
                        )}
                        
                        {/* Show notes and badges if present */}
                        {!isEditing && (item.notes || item.no_return || item.unlimited_inventory || item.replacement_cost > 0) && (
                          <div className="mt-2 flex items-center gap-2 flex-wrap">
                            {item.notes && <p className="text-xs text-slate-400">Note: {item.notes}</p>}
                            {item.replacement_cost > 0 && (
                              <span className="text-xs bg-red-600/20 text-red-400 px-2 py-0.5 rounded">
                                Replace: ${item.replacement_cost.toFixed(2)}
                              </span>
                            )}
                            {item.unlimited_inventory && (
                              <span className="text-xs bg-blue-600/20 text-blue-400 px-2 py-0.5 rounded">
                                Unlimited {item.max_per_order ? `(Max ${item.max_per_order}/order)` : ''}
                              </span>
                            )}
                            {item.no_return && (
                              <span className="text-xs bg-amber-600/20 text-amber-400 px-2 py-0.5 rounded">No Return</span>
                            )}
                          </div>
                        )}
                        
                        {/* Show reservations if any */}
                        {hasReservations && (
                          <div className="mt-2 pt-2 border-t border-slate-600/50">
                            <p className="text-xs text-orange-400 font-medium mb-1">Active Reservations:</p>
                            <div className="space-y-1">
                              {item.reservations?.map((res, idx) => (
                                <div key={idx} className="text-xs text-slate-400 flex items-center gap-2">
                                  <span className="text-orange-300">{res.order_id}</span>
                                  <span>×{res.quantity}</span>
                                  <span className="text-slate-500">|</span>
                                  <span>{STATUS_CONFIG[res.status]?.label || res.status}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </Card>
                    );
                  })}
                </div>
              )}
            </ScrollArea>
          </div>
        ) : (
        
        <div className="flex flex-col md:flex-row h-[calc(90vh-100px)] md:h-[calc(90vh-80px)] overflow-hidden">
          {/* Orders List - Full width on mobile, half on desktop */}
          <div className={`${selectedOrder ? 'hidden md:flex' : 'flex'} w-full md:w-1/2 border-b md:border-b-0 md:border-r border-slate-700 flex-col min-h-0`}>
            {/* Filters */}
            <div className="p-2 md:p-3 border-b border-slate-700 space-y-2">
              <div className="flex flex-col md:flex-row gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="Search orders..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10 bg-slate-700 border-slate-600 text-white h-9"
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={openManualOrderDialog}
                    className="bg-green-600 hover:bg-green-500 flex-1 md:flex-none"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    <span className="hidden md:inline">Manual Order</span>
                    <span className="md:hidden">New</span>
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setShowSettings(true)}
                    className="border-slate-600 text-slate-300"
                  >
                    <Settings className="h-4 w-4" />
                  </Button>
                  <Button
                    size="sm"
                    onClick={fetchOrders}
                    className="bg-purple-600 hover:bg-purple-500"
                  >
                    <RefreshCw className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              
              <div className="flex gap-1 flex-wrap overflow-x-auto pb-1">
                <Button
                  size="sm"
                  variant={statusFilter === 'all' ? 'default' : 'ghost'}
                  onClick={() => setStatusFilter('all')}
                  className={`h-7 text-xs ${statusFilter === 'all' ? 'bg-purple-600' : 'text-slate-300'}`}
                >
                  All
                </Button>
                {STATUSES.filter(s => s !== 'cancelled').map(status => {
                  const config = STATUS_CONFIG[status];
                  return (
                    <Button
                      key={status}
                      size="sm"
                      variant={statusFilter === status ? 'default' : 'ghost'}
                      onClick={() => setStatusFilter(status)}
                      className={`h-7 text-xs ${statusFilter === status ? config.bg : 'text-slate-300'}`}
                    >
                      {config.label}
                    </Button>
                  );
                })}
                {/* Cancelled tab - separate from main flow */}
                <Button
                  size="sm"
                  variant={statusFilter === 'cancelled' ? 'default' : 'ghost'}
                  onClick={() => setStatusFilter('cancelled')}
                  className={`h-7 text-xs ${statusFilter === 'cancelled' ? 'bg-red-600' : 'text-slate-500'}`}
                >
                  Cancelled
                </Button>
              </div>
            </div>
            
            {/* Orders List */}
            <ScrollArea className="flex-1">
              {loading ? (
                <div className="flex items-center justify-center h-40">
                  <Loader2 className="h-8 w-8 animate-spin text-purple-400" />
                </div>
              ) : filteredOrders.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-40 text-slate-400">
                  <Package className="h-10 w-10 mb-2 opacity-50" />
                  <p>No orders found</p>
                </div>
              ) : (
                <div className="p-2 space-y-2">
                  {filteredOrders
                    .slice() // Create a copy to avoid mutating state
                    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at)) // Newest first
                    .map(order => {
                    const config = STATUS_CONFIG[order.status] || STATUS_CONFIG.received;
                    const Icon = config.icon;
                    const nextStatus = getNextStatus(order.status);
                    const nextConfig = nextStatus ? STATUS_CONFIG[nextStatus] : null;
                    const isCancelled = order.status === 'cancelled';
                    
                    return (
                      <div
                        key={order.order_id}
                        onClick={() => viewOrder(order.order_id)}
                        className={`p-3 rounded-lg cursor-pointer transition-colors border ${
                          selectedOrder?.order_id === order.order_id 
                            ? 'bg-purple-900/30 border-purple-500/50' 
                            : isCancelled
                            ? 'bg-red-900/20 border-red-500/30 hover:bg-red-900/30'
                            : 'bg-slate-700/50 border-slate-600/50 hover:bg-slate-700'
                        }`}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <p className={`font-medium text-sm ${isCancelled ? 'text-red-400 line-through' : 'text-white'}`}>{order.order_id}</p>
                            <p className={`text-xs ${isCancelled ? 'text-red-400/70 line-through' : 'text-slate-400'}`}>{order.customer?.name}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            {/* eCards Ordered Indicator */}
                            {order.ecards_ordered && (
                              <div className="flex items-center gap-1 px-2 py-1 rounded bg-blue-900/30 border border-blue-500/30" title="eCards have been ordered">
                                <CreditCard className="h-3 w-3 text-blue-400" />
                                <span className="text-xs text-blue-400">eCards</span>
                              </div>
                            )}
                            {/* Has eCards but NOT ordered yet */}
                            {!order.ecards_ordered && order.items?.some(i => i.type === 'ecard') && (
                              <div className="flex items-center gap-1 px-2 py-1 rounded bg-amber-900/30 border border-amber-500/30" title="eCards pending order">
                                <CreditCard className="h-3 w-3 text-amber-400" />
                                <span className="text-xs text-amber-400">Pending</span>
                              </div>
                            )}
                            <div className={`flex items-center gap-1 px-2 py-1 rounded ${config.bg} ${config.border} border`}>
                              <Icon className={`h-3 w-3 ${config.color}`} />
                              <span className={`text-xs ${config.color}`}>{config.label}</span>
                            </div>
                            {/* Quick Advance Button */}
                            {nextStatus && (
                              <Button
                                size="sm"
                                onClick={(e) => openQuickStatusModal(order, e)}
                                className={`h-7 px-2 ${nextConfig?.bg} hover:opacity-80 border ${nextConfig?.border}`}
                                title={`Advance to ${nextConfig?.label}`}
                                data-testid={`quick-advance-${order.order_id}`}
                              >
                                <ArrowRight className={`h-3 w-3 ${nextConfig?.color}`} />
                              </Button>
                            )}
                          </div>
                        </div>
                        <div className={`flex justify-between text-xs ${isCancelled ? 'text-red-400/70 line-through' : 'text-slate-400'}`}>
                          <span>{order.items?.length || 0} item(s)</span>
                          <span>${order.total?.toFixed(2)}</span>
                        </div>
                        <p className={`text-xs mt-1 ${isCancelled ? 'text-red-400/50' : 'text-slate-500'}`}>{formatDate(order.created_at)}</p>
                      </div>
                    );
                  })}
                </div>
              )}
            </ScrollArea>
          </div>
          
          {/* Order Detail - Full width on mobile when selected, half on desktop */}
          <div className={`${selectedOrder ? 'flex' : 'hidden md:flex'} w-full md:w-1/2 flex-col min-h-0 overflow-hidden`}>
            {selectedOrder ? (
              <>
                <div className="p-2 md:p-3 border-b border-slate-700 flex items-center justify-between gap-2">
                  {/* Back button for mobile */}
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setSelectedOrder(null)}
                    className="md:hidden text-slate-400 h-8 px-2"
                  >
                    <ChevronRight className="h-4 w-4 rotate-180" />
                    Back
                  </Button>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-white font-medium truncate">{selectedOrder.order_id}</h3>
                    <p className="text-xs text-slate-400">{formatDate(selectedOrder.created_at)}</p>
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => getPDF('checkout')}
                      disabled={!['delivered', 'returned', 'complete'].includes(selectedOrder.status)}
                      className={`border-slate-600 h-8 px-2 ${['delivered', 'returned', 'complete'].includes(selectedOrder.status) ? 'text-slate-300' : 'text-slate-500 opacity-50'}`}
                      title={!['delivered', 'returned', 'complete'].includes(selectedOrder.status) ? 'Available after delivery' : 'View Checkout PDF'}
                    >
                      <Download className="h-3 w-3" />
                      <span className="hidden lg:inline ml-1">Check-Out</span>
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => getPDF('checkin')}
                      disabled={selectedOrder.status !== 'complete'}
                      className={`border-slate-600 h-8 px-2 ${selectedOrder.status === 'complete' ? 'text-slate-300' : 'text-slate-500 opacity-50'}`}
                      title={selectedOrder.status !== 'complete' ? 'Available after inspection' : 'View Check-In PDF'}
                    >
                      <Download className="h-3 w-3" />
                      <span className="hidden lg:inline ml-1">Check-In</span>
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={getHistoryPDF}
                      className="border-slate-600 text-slate-300 h-8 px-2"
                      title="View Full History Report"
                    >
                      <FileText className="h-3 w-3" />
                      <span className="hidden lg:inline ml-1">History</span>
                    </Button>
                  </div>
                </div>
                
                <ScrollArea className="flex-1 p-2 md:p-3">
                  <Tabs defaultValue="details" className="w-full">
                    <TabsList className="bg-slate-700 w-full">
                      <TabsTrigger value="details" className="flex-1 text-xs md:text-sm">Details</TabsTrigger>
                      <TabsTrigger value="items" className="flex-1 text-xs md:text-sm">Items</TabsTrigger>
                      <TabsTrigger value="notes" className="flex-1 text-xs md:text-sm">Notes</TabsTrigger>
                      <TabsTrigger value="history" className="flex-1 text-xs md:text-sm">History</TabsTrigger>
                    </TabsList>
                    
                    <TabsContent value="details" className="space-y-4 mt-4">
                      {/* Status */}
                      <div className={`p-3 rounded-lg ${STATUS_CONFIG[selectedOrder.status]?.bg} border ${STATUS_CONFIG[selectedOrder.status]?.border}`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            {(() => {
                              const Icon = STATUS_CONFIG[selectedOrder.status]?.icon || Package;
                              return <Icon className={`h-5 w-5 ${STATUS_CONFIG[selectedOrder.status]?.color}`} />;
                            })()}
                            <span className={`font-medium ${STATUS_CONFIG[selectedOrder.status]?.color}`}>
                              {STATUS_CONFIG[selectedOrder.status]?.label}
                            </span>
                          </div>
                          {selectedOrder.status !== 'complete' && selectedOrder.status !== 'cancelled' && (
                            <Button
                              size="sm"
                              onClick={() => {
                                setNewStatus(STATUSES[STATUSES.indexOf(selectedOrder.status) + 1] || '');
                                setShowStatusUpdate(true);
                              }}
                              className="bg-purple-600 hover:bg-purple-500 h-7"
                            >
                              Update Status
                            </Button>
                          )}
                          {(selectedOrder.status === 'complete' || selectedOrder.status === 'cancelled') && (
                            <Button
                              size="sm"
                              onClick={() => {
                                setNewStatus('inspected'); // Default to inspected when reopening
                                setShowStatusUpdate(true);
                              }}
                              className="bg-amber-600 hover:bg-amber-500 h-7"
                              data-testid="reopen-order"
                            >
                              <RotateCcw className="h-3 w-3 mr-1" />
                              Reopen Order
                            </Button>
                          )}
                        </div>
                      </div>
                      
                      {/* Customer */}
                      <Card className="bg-slate-700/50 border-slate-600 p-3">
                        <h4 className="text-sm font-medium text-white mb-2 flex items-center gap-2">
                          <User className="h-4 w-4 text-blue-400" />
                          Customer
                        </h4>
                        <div className="space-y-1 text-sm">
                          <p className="text-white">{selectedOrder.customer?.name}</p>
                          <p className="text-slate-400 flex items-center gap-1">
                            <Mail className="h-3 w-3" />
                            {selectedOrder.customer?.email}
                          </p>
                          <p className="text-slate-400 flex items-center gap-1">
                            <Phone className="h-3 w-3" />
                            {selectedOrder.customer?.phone}
                          </p>
                          {selectedOrder.customer?.instructor_number && (
                            <p className="text-slate-400">ID: {selectedOrder.customer.instructor_number}</p>
                          )}
                        </div>
                      </Card>
                      
                      {/* Schedule */}
                      <Card className="bg-slate-700/50 border-slate-600 p-3">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="text-sm font-medium text-white flex items-center gap-2">
                            <Calendar className="h-4 w-4 text-green-400" />
                            Schedule
                          </h4>
                          {selectedOrder.status !== 'complete' && selectedOrder.status !== 'cancelled' && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={openEditScheduleDialog}
                              className="text-green-400 hover:text-green-300 h-6 px-2"
                              title="Edit pickup/return times"
                            >
                              <Edit2 className="h-3 w-3 mr-1" />
                              Edit
                            </Button>
                          )}
                        </div>
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div>
                            <p className="text-slate-400">Pickup</p>
                            <p className="text-white">{formatDate(selectedOrder.pickup_datetime)}</p>
                          </div>
                          <div>
                            <p className="text-slate-400">Return</p>
                            <p className="text-white">{formatDate(selectedOrder.return_datetime)}</p>
                          </div>
                        </div>
                        {selectedOrder.calendar_synced_at && (
                          <p className="text-xs text-slate-500 mt-2">
                            Last synced from calendar: {new Date(selectedOrder.calendar_synced_at).toLocaleString()}
                          </p>
                        )}
                      </Card>
                      
                      {/* Locations */}
                      <Card className="bg-slate-700/50 border-slate-600 p-3">
                        <h4 className="text-sm font-medium text-white mb-2 flex items-center gap-2">
                          <MapPin className="h-4 w-4 text-orange-400" />
                          Locations
                        </h4>
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div>
                            <p className="text-slate-400">Pickup</p>
                            <p className="text-white">
                              {selectedOrder.pickup_location?.predefined_name || selectedOrder.pickup_location?.custom_address || '-'}
                            </p>
                          </div>
                          <div>
                            <p className="text-slate-400">Return</p>
                            <p className="text-white">
                              {selectedOrder.dropoff_location?.predefined_name || selectedOrder.dropoff_location?.custom_address || '-'}
                            </p>
                          </div>
                        </div>
                      </Card>
                      
                      {/* Totals */}
                      <Card className="bg-slate-700/50 border-slate-600 p-3">
                        <h4 className="text-sm font-medium text-white mb-2 flex items-center gap-2">
                          <DollarSign className="h-4 w-4 text-emerald-400" />
                          Payment
                        </h4>
                        <div className="space-y-1 text-sm">
                          <div className="flex justify-between">
                            <span className="text-slate-400">Subtotal</span>
                            <span className="text-white">${(selectedOrder.subtotal || 0).toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-slate-400">Deposit</span>
                            <span className="text-white">${(selectedOrder.deposit_amount || 0).toFixed(2)}</span>
                          </div>
                          {(selectedOrder.late_fee_charged || 0) > 0 && (
                            <div className="flex justify-between text-amber-400">
                              <span>Late Return Fee</span>
                              <span>-${(selectedOrder.late_fee_charged || 0).toFixed(2)}</span>
                            </div>
                          )}
                          {(selectedOrder.late_fee_charged || 0) > 0 && (
                            <div className="flex justify-between text-sm">
                              <span className="text-slate-500">Deposit After Late Fee</span>
                              <span className="text-slate-300">${((selectedOrder.deposit_amount || 0) - (selectedOrder.late_fee_charged || 0)).toFixed(2)}</span>
                            </div>
                          )}
                          <div className="flex justify-between font-medium pt-1 border-t border-slate-600">
                            <span className="text-white">Total</span>
                            <span className="text-emerald-400">${(selectedOrder.total || 0).toFixed(2)}</span>
                          </div>
                          {selectedOrder.payment_info?.transaction_id && (
                            <p className="text-xs text-slate-500 mt-2">
                              Transaction: {selectedOrder.payment_info.transaction_id}
                            </p>
                          )}
                        </div>
                        
                        {/* Refund section */}
                        {selectedOrder.status === 'inspected' && !selectedOrder.refund_info && (
                          <Button
                            size="sm"
                            onClick={() => {
                              // Use deposit after late fee deduction
                              const depositAfterLateFee = (selectedOrder.deposit_amount || 0) - (selectedOrder.late_fee_charged || 0);
                              setRefundAmount(Math.max(0, depositAfterLateFee));
                              setRefundType('full');
                              setRefundNotes('');
                              setShowRefundDialog(true);
                            }}
                            className="w-full mt-3 bg-emerald-600 hover:bg-emerald-500"
                          >
                            Process Deposit Refund
                          </Button>
                        )}
                        
                        {selectedOrder.refund_info && (
                          <div className="mt-3 p-2 bg-emerald-900/30 rounded border border-emerald-700/50">
                            <p className="text-emerald-400 text-sm font-medium">Refund Processed</p>
                            <p className="text-emerald-300 text-xs">
                              {selectedOrder.refund_info.refund_type || 'Deposit'} refund: ${(selectedOrder.refund_info.refund_amount || selectedOrder.refund_info.total_refund || 0).toFixed(2)}
                            </p>
                            {selectedOrder.refund_info.refund_method && (
                              <p className="text-slate-400 text-xs">Method: {selectedOrder.refund_info.refund_method}</p>
                            )}
                            {selectedOrder.refund_info.notes && (
                              <p className="text-slate-400 text-xs mt-1">{selectedOrder.refund_info.notes}</p>
                            )}
                          </div>
                        )}
                      </Card>
                      
                      {/* Notes */}
                      {selectedOrder.notes && (
                        <Card className="bg-slate-700/50 border-slate-600 p-3">
                          <h4 className="text-sm font-medium text-white mb-2 flex items-center gap-2">
                            <FileText className="h-4 w-4 text-slate-400" />
                            Customer Notes
                          </h4>
                          <p className="text-sm text-slate-300">{selectedOrder.notes}</p>
                        </Card>
                      )}
                      
                      {/* eCards Status */}
                      {selectedOrder.items?.some(i => i.type === 'ecard') && (
                        <Card className={`border p-3 ${selectedOrder.ecards_ordered ? 'bg-blue-900/20 border-blue-500/30' : 'bg-amber-900/20 border-amber-500/30'}`}>
                          <h4 className="text-sm font-medium text-white mb-2 flex items-center gap-2">
                            <CreditCard className={`h-4 w-4 ${selectedOrder.ecards_ordered ? 'text-blue-400' : 'text-amber-400'}`} />
                            eCards Status
                          </h4>
                          <div className="space-y-1 text-sm">
                            {selectedOrder.ecards_ordered ? (
                              <>
                                <p className="text-blue-300 flex items-center gap-2">
                                  <CheckCircle className="h-4 w-4" />
                                  eCards Ordered Successfully
                                </p>
                                {selectedOrder.ecards_order_id && (
                                  <p className="text-slate-400">Order ID: {selectedOrder.ecards_order_id}</p>
                                )}
                                {selectedOrder.ecards_supplier_order && (
                                  <p className="text-slate-400">CPR Suppliers Order #: {selectedOrder.ecards_supplier_order}</p>
                                )}
                                {selectedOrder.ecards_ordered_at && (
                                  <p className="text-slate-500 text-xs">Ordered: {new Date(selectedOrder.ecards_ordered_at).toLocaleString()}</p>
                                )}
                              </>
                            ) : (
                              <p className="text-amber-300 flex items-center gap-2">
                                <AlertCircle className="h-4 w-4" />
                                eCards Pending - Not Yet Ordered
                              </p>
                            )}
                          </div>
                        </Card>
                      )}
                      
                      {/* Order Notes / Activity Log */}
                      {selectedOrder.order_notes && selectedOrder.order_notes.length > 0 && (
                        <Card className="bg-slate-700/50 border-slate-600 p-3">
                          <h4 className="text-sm font-medium text-white mb-2 flex items-center gap-2">
                            <FileText className="h-4 w-4 text-purple-400" />
                            Activity Log
                          </h4>
                          <div className="space-y-1">
                            {selectedOrder.order_notes.map((note, idx) => (
                              <p key={note.id || idx} className="text-sm text-slate-400">
                                {typeof note === 'string' ? note : (note.text || note.message || JSON.stringify(note))}
                              </p>
                            ))}
                          </div>
                        </Card>
                      )}
                    </TabsContent>
                    
                    <TabsContent value="items" className="mt-4">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-sm font-medium text-white">Order Items</h4>
                        {!editingItems ? (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={startEditingItems}
                            className="border-slate-600 text-slate-300 h-7"
                          >
                            <Edit2 className="h-3 w-3 mr-1" />
                            Edit Items
                          </Button>
                        ) : (
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setEditingItems(false)}
                              className="text-slate-400 h-7"
                            >
                              Cancel
                            </Button>
                            <Button
                              size="sm"
                              onClick={handleSaveItems}
                              disabled={savingItems}
                              className="bg-emerald-600 hover:bg-emerald-500 h-7"
                            >
                              {savingItems ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3 mr-1" />}
                              Save
                            </Button>
                          </div>
                        )}
                      </div>
                      
                      <div className="space-y-2">
                        {(editingItems ? tempItems : selectedOrder.items || []).map((item, index) => (
                          <Card key={index} className="bg-slate-700/50 border-slate-600 p-3">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <p className="text-white font-medium text-sm">{item.name}</p>
                                <p className="text-xs text-slate-400">
                                  ${item.unit_price?.toFixed(2)} × {item.quantity} 
                                  {item.days > 1 ? ` × ${item.days} days` : ''}
                                  {' = '}
                                  <span className="text-purple-400">
                                    ${(item.unit_price * item.quantity * (item.days || 1)).toFixed(2)}
                                  </span>
                                </p>
                              </div>
                              
                              {editingItems && (
                                <div className="flex items-center gap-2">
                                  <Input
                                    type="number"
                                    min="1"
                                    value={item.quantity}
                                    onChange={(e) => updateTempItem(index, 'quantity', e.target.value === '' ? '' : parseInt(e.target.value))}
                                    onBlur={(e) => {
                                      const val = parseInt(e.target.value);
                                      if (isNaN(val) || val < 1) updateTempItem(index, 'quantity', 1);
                                    }}
                                    className="w-16 h-7 bg-slate-600 border-slate-500 text-white text-center text-sm"
                                  />
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => removeTempItem(index)}
                                    className="h-7 w-7 p-0 text-red-400"
                                  >
                                    <Trash2 className="h-3 w-3" />
                                  </Button>
                                </div>
                              )}
                            </div>
                            
                            {/* Condition notes */}
                            {editingItems && (
                              <div className="mt-2 grid grid-cols-2 gap-2">
                                <div>
                                  <Label className="text-xs text-slate-400">Condition Out</Label>
                                  <Input
                                    placeholder="Condition when checked out"
                                    value={item.condition_out || ''}
                                    onChange={(e) => updateTempItem(index, 'condition_out', e.target.value)}
                                    className="h-7 bg-slate-600 border-slate-500 text-white text-xs"
                                  />
                                </div>
                                <div>
                                  <Label className="text-xs text-slate-400">Condition In</Label>
                                  <Input
                                    placeholder="Condition when returned"
                                    value={item.condition_in || ''}
                                    onChange={(e) => updateTempItem(index, 'condition_in', e.target.value)}
                                    className="h-7 bg-slate-600 border-slate-500 text-white text-xs"
                                  />
                                </div>
                                <div className="col-span-2">
                                  <Label className="text-xs text-slate-400">Serial Numbers</Label>
                                  <Input
                                    placeholder="Serial numbers (comma separated)"
                                    value={item.serial_numbers?.join(', ') || ''}
                                    onChange={(e) => updateTempItem(index, 'serial_numbers', e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
                                    className="h-7 bg-slate-600 border-slate-500 text-white text-xs"
                                  />
                                </div>
                              </div>
                            )}
                            
                            {!editingItems && (item.condition_out || item.condition_in || item.serial_numbers?.length > 0) && (
                              <div className="mt-2 text-xs text-slate-400 space-y-1">
                                {item.condition_out && <p>Out: {item.condition_out}</p>}
                                {item.condition_in && <p>In: {item.condition_in}</p>}
                                {item.serial_numbers?.length > 0 && <p>S/N: {item.serial_numbers.join(', ')}</p>}
                              </div>
                            )}
                          </Card>
                        ))}
                      </div>
                    </TabsContent>
                    
                    {/* Notes Tab */}
                    <TabsContent value="notes" className="mt-4">
                      <div className="space-y-4">
                        {/* Add Note Form */}
                        <div className="bg-slate-700/50 rounded-lg p-4">
                          <h4 className="text-sm font-medium text-white mb-3 flex items-center gap-2">
                            <MessageSquare className="h-4 w-4 text-slate-400" />
                            Add Note
                          </h4>
                          
                          <div className="space-y-3">
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant={newNoteType === 'admin' ? 'default' : 'outline'}
                                onClick={() => setNewNoteType('admin')}
                                className={newNoteType === 'admin' ? 'bg-amber-600 hover:bg-amber-500' : 'border-slate-600 text-slate-300'}
                              >
                                Admin Note
                              </Button>
                              <Button
                                size="sm"
                                variant={newNoteType === 'customer' ? 'default' : 'outline'}
                                onClick={() => setNewNoteType('customer')}
                                className={newNoteType === 'customer' ? 'bg-green-600 hover:bg-green-500' : 'border-slate-600 text-slate-300'}
                              >
                                Customer Note
                              </Button>
                            </div>
                            
                            <Textarea
                              value={newNoteText}
                              onChange={(e) => setNewNoteText(e.target.value)}
                              placeholder={newNoteType === 'admin' ? 'Internal note (only visible to admins)...' : 'Note to customer...'}
                              className="bg-slate-700 border-slate-600 text-white min-h-[80px]"
                            />
                            
                            {newNoteType === 'customer' && (
                              <div className="flex items-center gap-2">
                                <Checkbox
                                  id="send-email"
                                  checked={sendNoteEmail}
                                  onCheckedChange={setSendNoteEmail}
                                  className="border-slate-500 data-[state=checked]:bg-green-600"
                                />
                                <Label htmlFor="send-email" className="text-sm text-slate-300 cursor-pointer">
                                  Send email notification to customer
                                </Label>
                              </div>
                            )}
                            
                            <Button
                              size="sm"
                              onClick={addNote}
                              disabled={!newNoteText.trim()}
                              className="bg-purple-600 hover:bg-purple-500"
                            >
                              <Plus className="h-3 w-3 mr-1" />
                              Add Note
                            </Button>
                          </div>
                        </div>
                        
                        {/* Notes List */}
                        <div className="space-y-2">
                          <h4 className="text-sm font-medium text-slate-400">
                            Notes ({orderNotes.length})
                          </h4>
                          
                          {orderNotes.length === 0 ? (
                            <p className="text-sm text-slate-500 text-center py-4">No notes yet</p>
                          ) : (
                            orderNotes.slice().sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).map((note) => (
                              <div 
                                key={note.id} 
                                className={`rounded-lg p-3 border-l-4 ${
                                  note.type === 'admin' 
                                    ? 'bg-amber-900/20 border-amber-500' 
                                    : 'bg-green-900/20 border-green-500'
                                }`}
                              >
                                <div className="flex items-center justify-between mb-1">
                                  <span className={`text-xs font-medium ${note.type === 'admin' ? 'text-amber-400' : 'text-green-400'}`}>
                                    {note.type === 'admin' ? '🔒 Admin Note' : '📧 Customer Note'}
                                  </span>
                                  <span className="text-xs text-slate-500">
                                    {formatDate(note.created_at)}
                                  </span>
                                </div>
                                <p className="text-sm text-slate-300">{note.text}</p>
                                {note.email_sent && (
                                  <p className="text-xs text-green-400 mt-1">
                                    ✉️ Emailed to {note.email_sent_to}
                                  </p>
                                )}
                              </div>
                            ))
                          )}
                        </div>
                      </div>
                    </TabsContent>
                    
                    <TabsContent value="history" className="mt-4">
                      <h4 className="text-sm font-medium text-white mb-3 flex items-center gap-2">
                        <History className="h-4 w-4 text-slate-400" />
                        Audit Log
                      </h4>
                      
                      <div className="space-y-2">
                        {(selectedOrder.audit_log || []).map((log, index) => (
                          <div key={index} className="bg-slate-700/50 rounded-lg p-2 text-sm">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-purple-400 font-medium capitalize">
                                {log.action?.replace(/_/g, ' ')}
                              </span>
                              <span className="text-xs text-slate-500">{formatDate(log.timestamp)}</span>
                            </div>
                            <p className="text-slate-300 text-xs">
                              {JSON.stringify(log.details)}
                            </p>
                            <p className="text-xs text-slate-500">By: {log.performed_by}</p>
                          </div>
                        ))}
                      </div>
                    </TabsContent>
                  </Tabs>
                </ScrollArea>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-slate-400">
                <div className="text-center">
                  <Package className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>Select an order to view details</p>
                </div>
              </div>
            )}
          </div>
        </div>
        )}
      </DialogContent>
      
      {/* Status Update Dialog */}
      <Dialog open={showStatusUpdate} onOpenChange={setShowStatusUpdate}>
        <DialogContent className="bg-slate-800 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white">Update Order Status</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-slate-300">New Status</Label>
              <Select value={newStatus} onValueChange={setNewStatus}>
                <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUSES.map(status => (
                    <SelectItem key={status} value={status}>
                      {STATUS_CONFIG[status]?.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-slate-300">Notes (Optional)</Label>
              <textarea
                value={statusNotes}
                onChange={(e) => setStatusNotes(e.target.value)}
                placeholder="Add notes about this status change..."
                className="w-full h-20 bg-slate-700 border border-slate-600 rounded-lg p-2 text-white placeholder-slate-400 resize-none"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowStatusUpdate(false)} className="text-slate-300">
              Cancel
            </Button>
            <Button onClick={handleStatusUpdate} disabled={processingStatus} className="bg-purple-600 hover:bg-purple-500">
              {processingStatus ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Update Status
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Quick Status Advance Dialog */}
      <Dialog open={showQuickStatusModal} onOpenChange={setShowQuickStatusModal}>
        <DialogContent className="bg-slate-800 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <ArrowRight className="h-5 w-5 text-purple-400" />
              Advance Order Status
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Order: {quickStatusOrder?.order_id}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* Status transition display */}
            <div className="flex items-center justify-center gap-3 py-3">
              <div className={`flex items-center gap-2 px-3 py-2 rounded ${STATUS_CONFIG[quickStatusOrder?.status]?.bg} border ${STATUS_CONFIG[quickStatusOrder?.status]?.border}`}>
                {(() => {
                  const CurrentIcon = STATUS_CONFIG[quickStatusOrder?.status]?.icon || Package;
                  return <CurrentIcon className={`h-4 w-4 ${STATUS_CONFIG[quickStatusOrder?.status]?.color}`} />;
                })()}
                <span className={`text-sm ${STATUS_CONFIG[quickStatusOrder?.status]?.color}`}>
                  {STATUS_CONFIG[quickStatusOrder?.status]?.label}
                </span>
              </div>
              <ArrowRight className="h-5 w-5 text-slate-400" />
              <div className={`flex items-center gap-2 px-3 py-2 rounded ${STATUS_CONFIG[quickStatusOrder?.nextStatus]?.bg} border ${STATUS_CONFIG[quickStatusOrder?.nextStatus]?.border}`}>
                {(() => {
                  const NextIcon = STATUS_CONFIG[quickStatusOrder?.nextStatus]?.icon || Package;
                  return <NextIcon className={`h-4 w-4 ${STATUS_CONFIG[quickStatusOrder?.nextStatus]?.color}`} />;
                })()}
                <span className={`text-sm ${STATUS_CONFIG[quickStatusOrder?.nextStatus]?.color}`}>
                  {STATUS_CONFIG[quickStatusOrder?.nextStatus]?.label}
                </span>
              </div>
            </div>
            
            {/* Notes input */}
            <div>
              <Label className="text-slate-300">Notes (Optional)</Label>
              <textarea
                value={quickStatusNotes}
                onChange={(e) => setQuickStatusNotes(e.target.value)}
                placeholder="Add notes about this status change..."
                className="w-full h-20 bg-slate-700 border border-slate-600 rounded-lg p-2 text-white placeholder-slate-400 resize-none mt-1"
                data-testid="quick-status-notes"
              />
            </div>
            
            {/* Customer info */}
            <div className="bg-slate-700/50 rounded-lg p-3 text-sm">
              <p className="text-slate-400">Customer: <span className="text-white">{quickStatusOrder?.customer?.name}</span></p>
              <p className="text-slate-400">Items: <span className="text-white">{quickStatusOrder?.items?.length || 0}</span></p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowQuickStatusModal(false)} className="text-slate-300">
              Cancel
            </Button>
            <Button 
              onClick={handleQuickStatusUpdate} 
              disabled={processingQuickStatus} 
              className={`${STATUS_CONFIG[quickStatusOrder?.nextStatus]?.bg} hover:opacity-90`}
              data-testid="confirm-quick-status"
            >
              {processingQuickStatus ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Advance to {STATUS_CONFIG[quickStatusOrder?.nextStatus]?.label}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Return Checkpoint Dialog */}
      <Dialog open={showReturnModal} onOpenChange={setShowReturnModal}>
        <DialogContent className="bg-slate-800 border-slate-700 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <RotateCcw className="h-5 w-5 text-blue-400" />
              Process Equipment Return
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Confirm the return date and time. Late fees will be calculated automatically.
            </DialogDescription>
          </DialogHeader>
          
          {returnOrder && (
            <div className="space-y-4 py-2">
              {/* Order Info */}
              <div className="bg-slate-700/50 border border-slate-600 rounded-lg p-3 text-sm">
                <p className="text-slate-400">Order: <span className="text-white font-medium">{returnOrder.order_id}</span></p>
                <p className="text-slate-400">Customer: <span className="text-white">{returnOrder.customer?.name}</span></p>
              </div>
              
              {/* Scheduled Return Time */}
              <div className="bg-blue-900/30 border border-blue-500/30 rounded-lg p-3">
                <p className="text-blue-300 text-sm font-medium flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  Scheduled Return
                </p>
                <p className="text-white mt-1">
                  {returnOrder.return_datetime ? new Date(returnOrder.return_datetime).toLocaleString('en-US', {
                    timeZone: 'America/Los_Angeles',
                    dateStyle: 'medium',
                    timeStyle: 'short'
                  }) : 'Not set'}
                </p>
              </div>
              
              {/* Actual Return Time Inputs */}
              <div className="space-y-2">
                <Label className="text-slate-300">Actual Return Date & Time (PST)</Label>
                <div className="grid grid-cols-2 gap-3">
                  <Input
                    type="date"
                    value={actualReturnDate}
                    onChange={(e) => handleReturnDateTimeChange(e.target.value, null)}
                    className="bg-slate-700 border-slate-600 text-white"
                  />
                  <Input
                    type="time"
                    value={actualReturnTime}
                    onChange={(e) => handleReturnDateTimeChange(null, e.target.value)}
                    className="bg-slate-700 border-slate-600 text-white"
                  />
                </div>
              </div>
              
              {/* Late Fee Calculation */}
              {lateFeesData && (
                <div className={`rounded-lg p-4 ${lateFeesData.isLate ? 'bg-amber-900/40 border border-amber-500/50' : 'bg-green-900/30 border border-green-500/30'}`}>
                  {lateFeesData.isLate ? (
                    <>
                      <div className="flex items-center gap-2 text-amber-300 font-medium mb-3">
                        <AlertCircle className="h-5 w-5" />
                        Late Return Detected
                      </div>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between text-slate-300">
                          <span>Time Past Due:</span>
                          <span className="text-amber-200">{lateFeesData.diffHours} hours ({lateFeesData.diffMinutes} min)</span>
                        </div>
                        <div className="flex justify-between text-slate-300">
                          <span>Extra Days Charged:</span>
                          <span className="text-amber-200">{lateFeesData.extraDays} day{lateFeesData.extraDays > 1 ? 's' : ''}</span>
                        </div>
                        <div className="flex justify-between text-slate-300">
                          <span>Daily Rate:</span>
                          <span className="text-white">${lateFeesData.dailyRate.toFixed(2)}</span>
                        </div>
                        <hr className="border-amber-500/30 my-2" />
                        <div className="flex justify-between items-center">
                          <span className="text-slate-300">Late Fee:</span>
                          <div className="flex items-center gap-2">
                            <span className="text-slate-400">$</span>
                            <Input
                              type="number"
                              value={lateFeeAmount}
                              onChange={(e) => setLateFeeAmount(parseFloat(e.target.value) || 0)}
                              className="bg-slate-700 border-slate-600 text-white w-24 h-8"
                              step="0.01"
                              min="0"
                            />
                          </div>
                        </div>
                        {returnOrder.deposit_amount && (
                          <div className="flex justify-between text-slate-300 mt-2">
                            <span>Deposit Held:</span>
                            <span className="text-white">${parseFloat(returnOrder.deposit_amount).toFixed(2)}</span>
                          </div>
                        )}
                        {returnOrder.deposit_amount && lateFeeAmount > 0 && (
                          <div className="flex justify-between text-green-300 font-medium">
                            <span>Deposit to Refund:</span>
                            <span>${Math.max(0, parseFloat(returnOrder.deposit_amount) - lateFeeAmount).toFixed(2)}</span>
                          </div>
                        )}
                      </div>
                    </>
                  ) : (
                    <div className="flex items-center gap-2 text-green-300">
                      <Check className="h-5 w-5" />
                      <span>Returned on time (within 30 min grace period)</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          
          <DialogFooter className="flex gap-2">
            <Button 
              variant="ghost" 
              onClick={() => setShowReturnModal(false)} 
              className="text-slate-300"
            >
              Cancel
            </Button>
            
            {lateFeesData?.isLate && lateFeeAmount > 0 ? (
              <>
                <Button
                  variant="outline"
                  onClick={() => handleReturnSubmit(false)}
                  disabled={processingReturn}
                  className="border-slate-500 text-slate-300 hover:bg-slate-700"
                >
                  {processingReturn ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Bypass (No Charge)
                </Button>
                <Button
                  onClick={() => handleReturnSubmit(true)}
                  disabled={processingReturn}
                  className="bg-amber-600 hover:bg-amber-500"
                >
                  {processingReturn ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <DollarSign className="h-4 w-4 mr-2" />}
                  Charge ${lateFeeAmount.toFixed(2)}
                </Button>
              </>
            ) : (
              <Button
                onClick={() => handleReturnSubmit(false)}
                disabled={processingReturn}
                className="bg-blue-600 hover:bg-blue-500"
              >
                {processingReturn ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Check className="h-4 w-4 mr-2" />}
                Confirm Return
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Inspection Dialog */}
      <Dialog open={showInspectionModal} onOpenChange={setShowInspectionModal}>
        <DialogContent className="bg-slate-800 border-slate-700 max-w-2xl max-h-[85vh] flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="text-white flex items-center gap-2">
              <ClipboardCheck className="h-5 w-5 text-teal-400" />
              Equipment Inspection
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Order: {inspectionOrder?.order_id} | Customer: {inspectionOrder?.customer?.name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="overflow-y-auto flex-1 pr-2" style={{ maxHeight: 'calc(85vh - 180px)' }}>
            <div className="space-y-4 pb-4">
              {/* Summary */}
              <div className="bg-slate-700/50 rounded-lg p-3 text-sm">
                <p className="text-slate-400">Deposit Amount: <span className="text-white">${inspectionOrder?.deposit_amount?.toFixed(2)}</span></p>
                <p className="text-slate-400">Total Damage Charges: <span className="text-red-400">${inspectionItems.reduce((sum, item) => sum + (parseFloat(item.damage_charge) || 0), 0).toFixed(2)}</span></p>
              </div>
              
              {/* Items to inspect */}
              <div className="space-y-4">
                <Label className="text-slate-300 text-base font-semibold">Verify Each Item</Label>
                
                {inspectionItems.map((item, index) => {
                  const hasDiscrepancy = item.returned_quantity < item.rented_quantity;
                  const hasDamageCharge = parseFloat(item.damage_charge) > 0;
                  
                  return (
                    <div 
                      key={item.sku} 
                      className={`p-4 rounded-lg border ${
                        hasDiscrepancy || hasDamageCharge 
                          ? 'bg-red-900/20 border-red-500/50' 
                          : 'bg-slate-700/50 border-slate-600/50'
                      }`}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <p className="text-white font-medium">{item.name}</p>
                          <p className="text-slate-400 text-sm">SKU: {item.sku}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-slate-400 text-sm">Rented: <span className="text-white font-semibold">{item.rented_quantity}</span></p>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-slate-400 text-xs">Returned Quantity</Label>
                          <div className="flex items-center gap-2 mt-1">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => updateInspectionItem(index, 'returned_quantity', Math.max(0, item.returned_quantity - 1))}
                              className="h-8 w-8 p-0 border-slate-600"
                            >
                              <Minus className="h-4 w-4" />
                            </Button>
                            <Input
                              type="number"
                              min="0"
                              max={item.rented_quantity}
                              value={item.returned_quantity}
                              onChange={(e) => updateInspectionItem(index, 'returned_quantity', e.target.value === '' ? '' : parseInt(e.target.value))}
                              onBlur={(e) => {
                                const val = parseInt(e.target.value);
                                if (isNaN(val) || val < 0) updateInspectionItem(index, 'returned_quantity', 0);
                                else if (val > item.rented_quantity) updateInspectionItem(index, 'returned_quantity', item.rented_quantity);
                              }}
                              className="w-16 h-8 text-center bg-slate-700 border-slate-600 text-white"
                            />
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => updateInspectionItem(index, 'returned_quantity', Math.min(item.rented_quantity, item.returned_quantity + 1))}
                              className="h-8 w-8 p-0 border-slate-600"
                            >
                              <Plus className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                        
                        <div>
                          <Label className="text-slate-400 text-xs">Damage/Missing Charge ($)</Label>
                          {/* Show replacement cost reference when items are missing */}
                          {hasDiscrepancy && item.replacement_cost > 0 && (
                            <div className="text-xs text-amber-400 mt-0.5 mb-1">
                              Replacement: ${item.replacement_cost.toFixed(2)} × {item.rented_quantity - item.returned_quantity} = ${((item.rented_quantity - item.returned_quantity) * item.replacement_cost).toFixed(2)}
                            </div>
                          )}
                          {hasDiscrepancy && !item.replacement_cost && (
                            <div className="text-xs text-slate-500 mt-0.5 mb-1 italic">
                              No replacement cost set
                            </div>
                          )}
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            value={item.damage_charge}
                            onChange={(e) => updateInspectionItem(index, 'damage_charge', e.target.value === '' ? '' : e.target.value)}
                            onBlur={(e) => {
                              const val = parseFloat(e.target.value);
                              if (isNaN(val) || val < 0) updateInspectionItem(index, 'damage_charge', 0);
                            }}
                            className={`h-8 mt-1 bg-slate-700 border-slate-600 text-white ${hasDiscrepancy && item.replacement_cost > 0 ? 'border-amber-500/50' : ''}`}
                            placeholder="0.00"
                          />
                        </div>
                      </div>
                      
                      {/* Reason field - required if discrepancy or charge */}
                      {(hasDiscrepancy || hasDamageCharge) && (
                        <div className="mt-3">
                          <Label className="text-red-400 text-xs">
                            {hasDiscrepancy 
                              ? `* Reason for ${item.rented_quantity - item.returned_quantity} missing item(s)` 
                              : '* Reason for damage charge'}
                          </Label>
                          <Input
                            value={item.reason}
                            onChange={(e) => updateInspectionItem(index, 'reason', e.target.value)}
                            placeholder="E.g., Lost, Damaged beyond repair, Broken parts..."
                            className="mt-1 bg-slate-700 border-red-500/50 text-white"
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              
              {/* Overall notes */}
              <div>
                <Label className="text-slate-300">Inspection Notes (Optional)</Label>
                <textarea
                  value={inspectionNotes}
                  onChange={(e) => setInspectionNotes(e.target.value)}
                  placeholder="General notes about the inspection..."
                  className="w-full h-20 bg-slate-700 border border-slate-600 rounded-lg p-2 text-white placeholder-slate-400 resize-none mt-1"
                />
              </div>
              
              {/* Additional Fees Section */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-slate-300 text-base font-semibold flex items-center gap-2">
                    <DollarSign className="h-4 w-4" />
                    Additional Fees (Optional)
                  </Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setAdditionalFees([...additionalFees, { description: '', amount: 0 }])}
                    className="border-slate-600 text-slate-300 hover:bg-slate-700 h-8"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add Fee
                  </Button>
                </div>
                <p className="text-slate-400 text-xs">Add late return fees, cleaning fees, or any other charges</p>
                
                {additionalFees.length > 0 && (
                  <div className="space-y-2">
                    {additionalFees.map((fee, index) => (
                      <div key={index} className="flex gap-2 items-start bg-slate-700/50 p-3 rounded-lg">
                        <div className="flex-1">
                          <Input
                            placeholder="Fee description (e.g., Late Return Fee)"
                            value={fee.description}
                            onChange={(e) => {
                              const updated = [...additionalFees];
                              updated[index].description = e.target.value;
                              setAdditionalFees(updated);
                            }}
                            className="bg-slate-700 border-slate-600 text-white h-9 mb-2"
                          />
                          <div className="flex items-center gap-2">
                            <span className="text-slate-400">$</span>
                            <Input
                              type="number"
                              step="0.01"
                              min="0"
                              placeholder="0.00"
                              value={fee.amount || ''}
                              onChange={(e) => {
                                const updated = [...additionalFees];
                                updated[index].amount = parseFloat(e.target.value) || 0;
                                setAdditionalFees(updated);
                              }}
                              className="bg-slate-700 border-slate-600 text-white h-9 w-32"
                            />
                          </div>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setAdditionalFees(additionalFees.filter((_, i) => i !== index));
                          }}
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/20 h-8 w-8 p-0"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                    
                    {/* Additional Fees Total */}
                    <div className="flex justify-between text-sm pt-2 border-t border-slate-600">
                      <span className="text-slate-400">Additional Fees Total:</span>
                      <span className="text-amber-400 font-medium">
                        ${additionalFees.reduce((sum, fee) => sum + (parseFloat(fee.amount) || 0), 0).toFixed(2)}
                      </span>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Inspector Signature Section */}
              <div className="space-y-2">
                <Label className="text-slate-300 text-base font-semibold flex items-center gap-2">
                  <PenTool className="h-4 w-4" />
                  Inspector Signature (Required)
                </Label>
                
                {inspectorSignature ? (
                  <div 
                    className="border-2 border-green-500/50 rounded-lg bg-white p-2 cursor-pointer hover:border-green-400 transition-colors"
                    onClick={() => setShowInspectorSignatureModal(true)}
                  >
                    <img 
                      src={inspectorSignature} 
                      alt="Inspector signature" 
                      className="w-full h-[60px] object-contain"
                    />
                    <p className="text-green-600 text-xs text-center mt-1 flex items-center justify-center gap-1">
                      <Check className="h-3 w-3" /> Signature captured - tap to re-sign
                    </p>
                  </div>
                ) : (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowInspectorSignatureModal(true)}
                    className="w-full h-20 border-2 border-dashed border-slate-500 bg-slate-700/30 hover:bg-slate-700/50 hover:border-teal-500 text-slate-300 flex flex-col items-center justify-center gap-2"
                  >
                    <PenTool className="h-6 w-6" />
                    <span>Tap to Sign</span>
                  </Button>
                )}
              </div>
              
              {/* Summary of charges */}
              {(inspectionItems.some(item => item.returned_quantity < item.rented_quantity || parseFloat(item.damage_charge) > 0) || additionalFees.some(fee => parseFloat(fee.amount) > 0)) && (
                <div className="bg-amber-900/30 border border-amber-500/50 rounded-lg p-4">
                  <h4 className="text-amber-400 font-medium mb-2">Charge Summary</h4>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between text-slate-300">
                      <span>Total Damage Charges:</span>
                      <span className="text-red-400">${inspectionItems.reduce((sum, item) => sum + (parseFloat(item.damage_charge) || 0), 0).toFixed(2)}</span>
                    </div>
                    {additionalFees.some(fee => parseFloat(fee.amount) > 0) && (
                      <div className="flex justify-between text-slate-300">
                        <span>Additional Fees:</span>
                        <span className="text-red-400">${additionalFees.reduce((sum, fee) => sum + (parseFloat(fee.amount) || 0), 0).toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-slate-300 pt-1 border-t border-slate-600/50">
                      <span className="font-medium">Total Charges:</span>
                      <span className="text-red-400 font-medium">
                        ${(inspectionItems.reduce((sum, item) => sum + (parseFloat(item.damage_charge) || 0), 0) + 
                           additionalFees.reduce((sum, fee) => sum + (parseFloat(fee.amount) || 0), 0)).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between text-slate-300">
                      <span>Deposit Amount:</span>
                      <span>${inspectionOrder?.deposit_amount?.toFixed(2)}</span>
                    </div>
                    <hr className="border-slate-600 my-2" />
                    {(() => {
                      const totalDamage = inspectionItems.reduce((sum, item) => sum + (parseFloat(item.damage_charge) || 0), 0);
                      const totalAdditional = additionalFees.reduce((sum, fee) => sum + (parseFloat(fee.amount) || 0), 0);
                      const totalCharges = totalDamage + totalAdditional;
                      const deposit = inspectionOrder?.deposit_amount || 0;
                      const diff = deposit - totalCharges;
                      
                      if (diff >= 0) {
                        return (
                          <div className="flex justify-between text-emerald-400 font-medium">
                            <span>Refund to Customer:</span>
                            <span>${diff.toFixed(2)}</span>
                          </div>
                        );
                      } else {
                        return (
                          <div className="flex justify-between text-red-400 font-medium">
                            <span>Invoice to Customer:</span>
                            <span>${Math.abs(diff).toFixed(2)}</span>
                          </div>
                        );
                      }
                    })()}
                  </div>
                </div>
              )}
            </div>
          </div>
          
          <DialogFooter className="mt-4 flex-shrink-0">
            <Button variant="ghost" onClick={() => setShowInspectionModal(false)} className="text-slate-300">
              Cancel
            </Button>
            <Button 
              onClick={handleInspectionSubmit} 
              disabled={processingInspection}
              className="bg-teal-600 hover:bg-teal-500"
              data-testid="submit-inspection"
            >
              {processingInspection ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Complete Inspection
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Full-Screen Inspector Signature Modal */}
      <Dialog open={showInspectorSignatureModal} onOpenChange={setShowInspectorSignatureModal}>
        <DialogContent className="bg-slate-900 border-slate-700 max-w-none w-screen h-screen m-0 p-0 rounded-none flex flex-col">
          <div className="bg-slate-800 p-4 flex items-center justify-between border-b border-slate-700 flex-shrink-0">
            <div className="flex items-center gap-3">
              <PenTool className="h-6 w-6 text-teal-400" />
              <div>
                <h2 className="text-white text-lg font-semibold">Inspector Signature</h2>
                <p className="text-slate-400 text-sm">Sign to confirm inspection is complete</p>
              </div>
            </div>
            <Button
              variant="ghost"
              onClick={() => setShowInspectorSignatureModal(false)}
              className="text-slate-400 hover:text-white"
            >
              <X className="h-5 w-5" />
            </Button>
          </div>
          
          <div className="flex-1 p-4 min-h-0">
            <div className="h-full bg-white rounded-lg overflow-hidden relative">
              <canvas
                ref={inspectorSigCanvasRef}
                width={1200}
                height={500}
                className="w-full h-full cursor-crosshair touch-none"
                style={{ touchAction: 'none' }}
                onMouseDown={startDrawingInspectorSig}
                onMouseMove={drawInspectorSig}
                onMouseUp={stopDrawingInspectorSig}
                onMouseLeave={stopDrawingInspectorSig}
                onTouchStart={startDrawingInspectorSig}
                onTouchMove={drawInspectorSig}
                onTouchEnd={stopDrawingInspectorSig}
              />
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-20">
                <p className="text-slate-400 text-2xl font-light">Sign Here</p>
              </div>
            </div>
          </div>
          
          <div className="bg-slate-800 p-4 pb-8 flex items-center justify-between border-t border-slate-700 flex-shrink-0" style={{ paddingBottom: 'max(2rem, env(safe-area-inset-bottom, 2rem))' }}>
            <Button
              variant="outline"
              onClick={clearInspectorSignature}
              className="border-slate-600 text-slate-300 hover:bg-slate-700 h-12 px-6"
            >
              <RotateCcw className="h-5 w-5 mr-2" />
              Clear
            </Button>
            <Button
              onClick={saveInspectorSignature}
              className="bg-teal-600 hover:bg-teal-500 px-8 h-12 text-lg"
            >
              <Check className="h-5 w-5 mr-2" />
              Done
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* ==========================================
          FULFILLMENT CHECKPOINT MODAL (Processing → Ready)
          ========================================== */}
      <Dialog open={showFulfillmentModal} onOpenChange={setShowFulfillmentModal}>
        <DialogContent className="bg-slate-800 border-slate-700 max-w-2xl max-h-[85vh] flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="text-white flex items-center gap-2">
              <Package className="h-5 w-5 text-yellow-400" />
              Fulfillment Checkpoint
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Order: {fulfillmentOrder?.order_id} | Customer: {fulfillmentOrder?.customer?.name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="overflow-y-auto flex-1 pr-2" style={{ maxHeight: 'calc(85vh - 180px)' }}>
            <div className="space-y-4 pb-4">
              {/* Order Info */}
              <div className="bg-slate-700/50 rounded-lg p-3 text-sm grid grid-cols-2 gap-2">
                <div>
                  <p className="text-slate-400">Pickup Date:</p>
                  <p className="text-white">{fulfillmentOrder?.pickup_datetime?.split('T')[0] || fulfillmentOrder?.pickup_date || '-'}</p>
                </div>
                <div>
                  <p className="text-slate-400">Return Date:</p>
                  <p className="text-white">{fulfillmentOrder?.return_datetime?.split('T')[0] || fulfillmentOrder?.return_date || '-'}</p>
                </div>
                <div>
                  <p className="text-slate-400">Rental Days:</p>
                  <p className="text-white">{calculateFulfillmentRefund().days || 1}</p>
                </div>
                <div>
                  <p className="text-slate-400">Order Total:</p>
                  <p className="text-emerald-400">${(fulfillmentOrder?.total || 0).toFixed(2)}</p>
                </div>
              </div>
              
              {/* Items to fill */}
              <div className="space-y-3">
                <Label className="text-slate-300 text-base font-semibold">Verify Filled Quantities</Label>
                <p className="text-slate-400 text-xs">Enter how many of each item were actually provided to the customer.</p>
                
                {fulfillmentItems.map((item, index) => {
                  const isShort = (parseInt(item.filled_quantity) || 0) < item.ordered_quantity;
                  const unitPrice = parseFloat(item.unit_price) || 0;
                  const days = calculateFulfillmentRefund().days || 1;
                  const pricePerUnit = unitPrice * days;
                  
                  return (
                    <div 
                      key={item.sku} 
                      className={`p-4 rounded-lg border ${
                        isShort 
                          ? 'bg-amber-900/20 border-amber-500/50' 
                          : 'bg-slate-700/50 border-slate-600/50'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <p className="text-white font-medium">{item.name}</p>
                          <p className="text-slate-400 text-xs">
                            ${unitPrice.toFixed(2)}/day × {days} days = ${pricePerUnit.toFixed(2)}/unit
                          </p>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <p className="text-slate-400 text-xs">Ordered</p>
                            <p className="text-white font-bold text-lg">{item.ordered_quantity}</p>
                          </div>
                          <div className="text-center">
                            <p className="text-slate-400 text-xs">Filled</p>
                            <div className="flex items-center gap-1 mt-1">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => updateFulfillmentItem(index, Math.max(0, (parseInt(item.filled_quantity) || 0) - 1))}
                                className="h-8 w-8 p-0 border-slate-600"
                              >
                                <Minus className="h-4 w-4" />
                              </Button>
                              <Input
                                type="number"
                                min="0"
                                max={item.ordered_quantity}
                                value={item.filled_quantity}
                                onChange={(e) => updateFulfillmentItem(index, e.target.value)}
                                onBlur={(e) => {
                                  const val = parseInt(e.target.value);
                                  if (isNaN(val) || val < 0) updateFulfillmentItem(index, 0);
                                  else if (val > item.ordered_quantity) updateFulfillmentItem(index, item.ordered_quantity);
                                }}
                                className="w-16 h-8 text-center bg-slate-700 border-slate-600 text-white font-bold"
                              />
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => updateFulfillmentItem(index, Math.min(item.ordered_quantity, (parseInt(item.filled_quantity) || 0) + 1))}
                                className="h-8 w-8 p-0 border-slate-600"
                              >
                                <Plus className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {isShort && (
                        <div className="mt-2 space-y-2">
                          <div className="p-2 bg-amber-900/30 rounded flex items-center justify-between gap-2">
                            <div className="flex items-center gap-2">
                              <AlertTriangle className="h-4 w-4 text-amber-400" />
                              <span className="text-amber-300 text-sm">
                                {item.ordered_quantity - (parseInt(item.filled_quantity) || 0)} unit(s) short — 
                                Refund: ${((item.ordered_quantity - (parseInt(item.filled_quantity) || 0)) * unitPrice * days).toFixed(2)}
                              </span>
                            </div>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openReplacementPicker(item.sku)}
                              className="h-7 text-xs border-amber-500/50 text-amber-300 hover:bg-amber-900/50"
                            >
                              <ArrowLeftRight className="h-3 w-3 mr-1" />
                              Add Replacement
                            </Button>
                          </div>
                          
                          {/* Show replacements for this item */}
                          {replacementItems.filter(r => r.original_sku === item.sku).length > 0 && (
                            <div className="pl-4 space-y-1">
                              {replacementItems.filter(r => r.original_sku === item.sku).map((rep, repIndex) => {
                                const actualIndex = replacementItems.findIndex(r => r === rep);
                                const repCost = (parseInt(rep.quantity) || 0) * (parseFloat(rep.replacement_price) || 0) * days;
                                return (
                                  <div key={repIndex} className="flex items-center gap-2 p-2 bg-teal-900/30 border border-teal-500/30 rounded text-sm">
                                    <ArrowRight className="h-3 w-3 text-teal-400" />
                                    <span className="text-teal-300 flex-1">{rep.replacement_name}</span>
                                    <span className="text-slate-400 text-xs">${(rep.replacement_price || 0).toFixed(2)}/day</span>
                                    <div className="flex items-center gap-1">
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => updateReplacementQuantity(actualIndex, Math.max(1, (parseInt(rep.quantity) || 1) - 1))}
                                        className="h-6 w-6 p-0 text-slate-400"
                                      >
                                        <Minus className="h-3 w-3" />
                                      </Button>
                                      <span className="w-6 text-center text-white">{rep.quantity}</span>
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => updateReplacementQuantity(actualIndex, (parseInt(rep.quantity) || 1) + 1)}
                                        className="h-6 w-6 p-0 text-slate-400"
                                      >
                                        <Plus className="h-3 w-3" />
                                      </Button>
                                    </div>
                                    <span className="text-teal-400 font-medium text-xs w-16 text-right">+${repCost.toFixed(2)}</span>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => removeReplacementItem(actualIndex)}
                                      className="h-6 w-6 p-0 text-red-400 hover:text-red-300"
                                    >
                                      <X className="h-3 w-3" />
                                    </Button>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              
              {/* Shortage Warning & Note */}
              {calculateFulfillmentRefund().hasShortage && (
                <div className="space-y-3">
                  <div className="bg-amber-900/30 border border-amber-500/50 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="h-5 w-5 text-amber-400 mt-0.5" />
                      <div className="flex-1">
                        <p className="text-amber-300 font-medium">Order Cannot Be Fully Filled</p>
                        <div className="text-amber-200/70 text-sm mt-1 space-y-1">
                          <p>Shortage refund: <span className="font-bold text-amber-300">${(calculateFulfillmentRefund().refundAmount || 0).toFixed(2)}</span></p>
                          {calculateFulfillmentRefund().replacementCost > 0 && (
                            <p>Replacement cost: <span className="font-bold text-teal-300">-${(calculateFulfillmentRefund().replacementCost || 0).toFixed(2)}</span></p>
                          )}
                          {calculateFulfillmentRefund().netRefund > 0 ? (
                            <p className="text-base">Credit to apply: <span className="font-bold text-amber-300">${(calculateFulfillmentRefund().netRefund || 0).toFixed(2)}</span></p>
                          ) : calculateFulfillmentRefund().additionalCharge > 0 ? (
                            <p className="text-base text-green-300">No credit needed - Replacements cover shortage (+ <span className="font-bold">${(calculateFulfillmentRefund().additionalCharge || 0).toFixed(2)}</span> extra)</p>
                          ) : calculateFulfillmentRefund().replacementCost > 0 ? (
                            <p className="text-base text-green-300">No credit needed - Replacements offset shortage exactly</p>
                          ) : null}
                        </div>
                        {calculateFulfillmentRefund().netRefund > 0 && (
                          <div className="mt-2 p-2 bg-blue-900/30 border border-blue-500/30 rounded text-blue-300 text-xs">
                            <strong>Note:</strong> This credit will be combined with the deposit refund at order completion (single refund transaction).
                          </div>
                        )}
                        <ul className="mt-2 space-y-1">
                          {calculateFulfillmentRefund().shortages.map(s => (
                            <li key={s.sku} className="text-amber-200/70 text-xs">
                              • {s.name}: {s.shortage} short = ${(s.refund || 0).toFixed(2)} credit
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <Label className="text-amber-400 text-sm">* Reason for shortage (required)</Label>
                    <textarea
                      value={fulfillmentNote}
                      onChange={(e) => setFulfillmentNote(e.target.value)}
                      className="w-full h-20 mt-1 bg-slate-700 border border-amber-500/50 rounded-lg p-2 text-white placeholder-slate-400 resize-none text-sm"
                      placeholder="Explain why the order could not be fully filled..."
                    />
                  </div>
                </div>
              )}
              
              {/* Summary */}
              <div className="bg-slate-700/50 rounded-lg p-4">
                <h4 className="text-slate-300 font-medium mb-2">Order Summary</h4>
                <div className="space-y-1 text-sm">
                  {fulfillmentItems.map(item => (
                    <div key={item.sku} className="flex justify-between text-slate-400">
                      <span>{item.name}</span>
                      <span className={(parseInt(item.filled_quantity) || 0) < item.ordered_quantity ? 'text-amber-400' : 'text-white'}>
                        {item.filled_quantity || 0} / {item.ordered_quantity}
                      </span>
                    </div>
                  ))}
                  {replacementItems.length > 0 && (
                    <>
                      <div className="border-t border-slate-600 my-2" />
                      <p className="text-teal-400 text-xs font-medium">Replacements:</p>
                      {replacementItems.map((rep, i) => (
                        <div key={i} className="flex justify-between text-teal-300 text-xs pl-2">
                          <span>+ {rep.replacement_name}</span>
                          <span>× {rep.quantity}</span>
                        </div>
                      ))}
                    </>
                  )}
                  {calculateFulfillmentRefund().hasShortage && (
                    <>
                      <div className="border-t border-slate-600 my-2" />
                      {calculateFulfillmentRefund().netRefund > 0 ? (
                        <div className="flex justify-between text-amber-400 font-medium">
                          <span>Credit (held for final refund):</span>
                          <span>${(calculateFulfillmentRefund().netRefund || 0).toFixed(2)}</span>
                        </div>
                      ) : calculateFulfillmentRefund().additionalCharge > 0 ? (
                        <div className="flex justify-between text-green-400 font-medium">
                          <span>Additional Charge:</span>
                          <span>+${(calculateFulfillmentRefund().additionalCharge || 0).toFixed(2)}</span>
                        </div>
                      ) : (
                        <div className="flex justify-between text-green-400 font-medium">
                          <span>Credit:</span>
                          <span>$0.00 (offset by replacements)</span>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
          
          <DialogFooter className="mt-4 flex-wrap gap-2 flex-shrink-0">
            <Button variant="ghost" onClick={() => setShowFulfillmentModal(false)} className="text-slate-300">
              Back
            </Button>
            <div className="flex-1" />
            {calculateFulfillmentRefund().hasShortage ? (
              <>
                <Button 
                  variant="outline"
                  onClick={() => handleFulfillmentSubmit(false)} 
                  disabled={processingFulfillment || !fulfillmentNote.trim()}
                  className="border-slate-600 text-slate-300"
                >
                  No Credit, Set Ready
                </Button>
                {calculateFulfillmentRefund().netRefund > 0 ? (
                  <Button 
                    onClick={() => handleFulfillmentSubmit(true)} 
                    disabled={processingFulfillment || !fulfillmentNote.trim()}
                    className="bg-amber-600 hover:bg-amber-500"
                  >
                    {processingFulfillment ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                    Hold ${(calculateFulfillmentRefund().netRefund || 0).toFixed(2)} Credit & Set Ready
                  </Button>
                ) : (
                  <Button 
                    onClick={() => handleFulfillmentSubmit(true)} 
                    disabled={processingFulfillment || !fulfillmentNote.trim()}
                    className="bg-green-600 hover:bg-green-500"
                  >
                    {processingFulfillment ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle className="h-4 w-4 mr-2" />}
                    {calculateFulfillmentRefund().additionalCharge > 0 
                      ? `Set Ready (+$${(calculateFulfillmentRefund().additionalCharge || 0).toFixed(2)} charge)`
                      : 'Set Ready (No Refund Needed)'
                    }
                  </Button>
                )}
              </>
            ) : (
              <Button 
                onClick={() => handleFulfillmentSubmit(false)} 
                disabled={processingFulfillment}
                className="bg-green-600 hover:bg-green-500"
              >
                {processingFulfillment ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle className="h-4 w-4 mr-2" />}
                Mark Ready for Pickup
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Replacement Picker Modal */}
      <Dialog open={showReplacementPicker} onOpenChange={setShowReplacementPicker}>
        <DialogContent className="bg-slate-800 border-slate-700 max-w-lg max-h-[70vh] flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="text-white flex items-center gap-2">
              <ArrowLeftRight className="h-5 w-5 text-teal-400" />
              Select Replacement Item
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Choose an alternative item to replace the shortage
            </DialogDescription>
          </DialogHeader>
          
          <div className="overflow-y-auto flex-1 pr-2" style={{ maxHeight: 'calc(70vh - 150px)' }}>
            <div className="space-y-2 pb-2">
              {availableReplacements.length === 0 ? (
                <p className="text-slate-400 text-center py-4">No rental products available</p>
              ) : (
                availableReplacements
                  .filter(p => p.sku !== replacementForSku) // Don't show the same item
                  .map(product => (
                    <div
                      key={product.sku}
                      onClick={() => addReplacementItem(product)}
                      className="p-3 bg-slate-700/50 border border-slate-600 rounded-lg cursor-pointer hover:bg-slate-700 hover:border-teal-500/50 transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-white font-medium">{product.name}</p>
                          <p className="text-slate-400 text-xs">{product.sku}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-teal-400 font-medium">${(parseFloat(product.price) || 0).toFixed(2)}/day</p>
                        </div>
                      </div>
                    </div>
                  ))
              )}
            </div>
          </div>
          
          <DialogFooter className="mt-4 flex-shrink-0">
            <Button variant="ghost" onClick={() => setShowReplacementPicker(false)} className="text-slate-300">
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* ==========================================
          DELIVERY CHECKPOINT MODAL (Ready → Delivered)
          ========================================== */}
      <Dialog open={showDeliveryModal} onOpenChange={setShowDeliveryModal}>
        <DialogContent className="bg-slate-800 border-slate-700 max-w-2xl max-h-[85vh] flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="text-white flex items-center gap-2">
              <Truck className="h-5 w-5 text-purple-400" />
              Equipment Pickup/Delivery Confirmation
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Order: {deliveryOrder?.order_id} | Customer: {deliveryOrder?.customer?.name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="overflow-y-auto flex-1 pr-2" style={{ maxHeight: 'calc(85vh - 180px)' }}>
            <div className="space-y-4 pb-4">
              {/* Order Info */}
              <div className="bg-slate-700/50 rounded-lg p-3">
                <h4 className="text-slate-300 font-medium mb-2">Order Details</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <p className="text-slate-400">Pickup Date:</p>
                    <p className="text-white">
                      {deliveryOrder?.pickup_datetime 
                        ? new Date(deliveryOrder.pickup_datetime).toLocaleDateString() + ' ' + new Date(deliveryOrder.pickup_datetime).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})
                        : deliveryOrder?.pickup_date || '-'}
                    </p>
                  </div>
                  <div>
                    <p className="text-slate-400">Return Date:</p>
                    <p className="text-white">
                      {deliveryOrder?.return_datetime 
                        ? new Date(deliveryOrder.return_datetime).toLocaleDateString() + ' ' + new Date(deliveryOrder.return_datetime).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})
                        : deliveryOrder?.return_date || '-'}
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Items being delivered with condition notes */}
              <div className="space-y-2">
                <Label className="text-slate-300 text-base font-semibold">Equipment Being Received</Label>
                
                <div className="bg-slate-700/30 rounded-lg divide-y divide-slate-700">
                  {(deliveryOrder?.items || []).filter(item => item.type !== 'ecard').map((item, index) => {
                    const filledQty = item.filled_quantity || item.quantity;
                    const orderedQty = item.original_quantity || item.quantity;
                    const isPartial = filledQty < orderedQty;
                    // Get replacement cost from inventory
                    const invItem = inventory.find(inv => inv.sku === item.sku);
                    const replacementCost = invItem?.replacement_cost || 0;
                    
                    return (
                      <div key={item.sku || index} className="p-3 space-y-2">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <p className="text-white">{item.name}</p>
                            {isPartial && (
                              <p className="text-amber-400 text-xs">
                                Originally ordered: {orderedQty} (Adjusted due to availability)
                              </p>
                            )}
                            {replacementCost > 0 && (
                              <p className="text-red-400 text-xs mt-1">
                                ⚠️ Replacement cost if not returned: ${(replacementCost * filledQty).toFixed(2)} {filledQty > 1 ? `($${replacementCost.toFixed(2)} each)` : ''}
                              </p>
                            )}
                          </div>
                          <div className={`text-lg font-bold ${isPartial ? 'text-amber-400' : 'text-white'}`}>
                            × {filledQty}
                          </div>
                        </div>
                        {/* Condition notes for this item */}
                        <Input
                          placeholder="Condition notes (e.g., minor scratches, working perfectly)"
                          value={deliveryItemConditions[item.sku] || ''}
                          onChange={(e) => setDeliveryItemConditions(prev => ({
                            ...prev,
                            [item.sku]: e.target.value
                          }))}
                          className="bg-slate-700/50 border-slate-600 text-white text-sm placeholder:text-slate-500"
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
              
              {/* General condition/delivery notes */}
              <div className="space-y-2">
                <Label className="text-slate-300 text-sm">General Checkout Notes</Label>
                <textarea
                  value={deliveryConditionNotes}
                  onChange={(e) => setDeliveryConditionNotes(e.target.value)}
                  placeholder="Any additional notes about equipment condition, delivery location, special instructions, etc."
                  className="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white text-sm placeholder:text-slate-500 min-h-[80px] resize-none focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>
              
              {/* Terms Agreement */}
              <div className="bg-purple-900/20 border border-purple-500/30 rounded-lg p-4">
                <h4 className="text-purple-300 font-medium mb-3">Rental Terms & Conditions</h4>
                <div className="text-slate-300 text-sm space-y-2 mb-4">
                  <p>By signing below, I acknowledge that:</p>
                  <ul className="list-disc list-inside space-y-1 text-slate-400">
                    <li>I have received all equipment listed above in good working condition</li>
                    <li>I am responsible for the care and return of all equipment</li>
                    <li>Equipment must be returned by the scheduled return date</li>
                    <li>I am responsible for any damage, loss, or late fees</li>
                    <li>The deposit will be refunded after successful equipment return and inspection</li>
                  </ul>
                </div>
                
                <div className="flex items-center gap-2">
                  <Checkbox 
                    id="agree-terms"
                    checked={deliveryAgreed}
                    onCheckedChange={setDeliveryAgreed}
                    className="border-purple-500 data-[state=checked]:bg-purple-600"
                  />
                  <Label htmlFor="agree-terms" className="text-purple-300 cursor-pointer">
                    I agree to the rental terms and conditions
                  </Label>
                </div>
              </div>
              
              {/* Signature Section - Button to open full-screen */}
              <div className="space-y-2">
                <Label className="text-slate-300 text-base font-semibold flex items-center gap-2">
                  <PenTool className="h-4 w-4" />
                  Customer Signature
                </Label>
                
                {deliverySignature ? (
                  // Show signature preview
                  <div 
                    className="border-2 border-green-500/50 rounded-lg bg-white p-2 cursor-pointer hover:border-green-400 transition-colors"
                    onClick={openSignatureModal}
                  >
                    <img 
                      src={deliverySignature} 
                      alt="Customer signature" 
                      className="w-full h-[80px] object-contain"
                    />
                    <p className="text-green-600 text-xs text-center mt-1 flex items-center justify-center gap-1">
                      <Check className="h-3 w-3" /> Signature captured - tap to re-sign
                    </p>
                  </div>
                ) : (
                  // Show button to open signature modal
                  <Button
                    type="button"
                    variant="outline"
                    onClick={openSignatureModal}
                    className="w-full h-24 border-2 border-dashed border-slate-500 bg-slate-700/30 hover:bg-slate-700/50 hover:border-purple-500 text-slate-300 flex flex-col items-center justify-center gap-2"
                  >
                    <PenTool className="h-8 w-8" />
                    <span>Tap to Sign</span>
                  </Button>
                )}
              </div>
            </div>
          </div>
          
          <DialogFooter className="mt-4 flex-shrink-0">
            <Button variant="ghost" onClick={() => setShowDeliveryModal(false)} className="text-slate-300">
              Cancel
            </Button>
            <Button 
              onClick={handleDeliverySubmit} 
              disabled={processingDelivery || !deliveryAgreed || !deliverySignature}
              className="bg-purple-600 hover:bg-purple-500"
            >
              {processingDelivery ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Truck className="h-4 w-4 mr-2" />}
              Confirm Equipment Delivered
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Full-Screen Signature Modal */}
      <Dialog open={showSignatureModal} onOpenChange={setShowSignatureModal}>
        <DialogContent className="bg-slate-900 border-slate-700 max-w-none w-screen h-screen m-0 p-0 rounded-none flex flex-col">
          <div className="bg-slate-800 p-4 flex items-center justify-between border-b border-slate-700 flex-shrink-0">
            <div className="flex items-center gap-3">
              <PenTool className="h-6 w-6 text-purple-400" />
              <div>
                <h2 className="text-white text-lg font-semibold">Sign Below</h2>
                <p className="text-slate-400 text-sm">Use your finger or stylus to sign</p>
              </div>
            </div>
            <Button
              variant="ghost"
              onClick={() => setShowSignatureModal(false)}
              className="text-slate-400 hover:text-white"
            >
              <X className="h-5 w-5" />
            </Button>
          </div>
          
          <div className="flex-1 p-4 min-h-0">
            <div className="h-full bg-white rounded-lg overflow-hidden relative">
              <canvas
                ref={signatureCanvasRef}
                width={1200}
                height={500}
                className="w-full h-full cursor-crosshair touch-none"
                style={{ touchAction: 'none' }}
                onMouseDown={startDrawing}
                onMouseMove={draw}
                onMouseUp={stopDrawing}
                onMouseLeave={stopDrawing}
                onTouchStart={startDrawing}
                onTouchMove={draw}
                onTouchEnd={stopDrawing}
              />
              {/* Sign here hint */}
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-20">
                <p className="text-slate-400 text-2xl font-light">Sign Here</p>
              </div>
            </div>
          </div>
          
          {/* Footer with extra bottom padding for device navigation buttons */}
          <div className="bg-slate-800 p-4 pb-8 flex items-center justify-between border-t border-slate-700 flex-shrink-0" style={{ paddingBottom: 'max(2rem, env(safe-area-inset-bottom, 2rem))' }}>
            <Button
              variant="outline"
              onClick={clearSignature}
              className="border-slate-600 text-slate-300 hover:bg-slate-700 h-12 px-6"
            >
              <RotateCcw className="h-5 w-5 mr-2" />
              Clear
            </Button>
            <Button
              onClick={saveSignature}
              className="bg-green-600 hover:bg-green-500 px-8 h-12 text-lg"
            >
              <Check className="h-5 w-5 mr-2" />
              Done
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Refund Dialog */}
      <Dialog open={showRefundDialog} onOpenChange={setShowRefundDialog}>
        <DialogContent className="bg-slate-800 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white">Process Deposit Refund</DialogTitle>
            <DialogDescription className="text-slate-400">
              Original deposit: ${(selectedOrder?.deposit_amount || 0).toFixed(2)}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-slate-300">Refund Type</Label>
              <Select value={refundType} onValueChange={(v) => {
                setRefundType(v);
                if (v === 'full') {
                  setRefundAmount(selectedOrder?.deposit_amount || 0);
                }
              }}>
                <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="full">Full Refund</SelectItem>
                  <SelectItem value="partial">Partial Refund</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-slate-300">Refund Amount</Label>
              <Input
                type="number"
                min="0"
                max={selectedOrder?.deposit_amount || 0}
                step="0.01"
                value={refundAmount}
                onChange={(e) => setRefundAmount(e.target.value)}
                disabled={refundType === 'full'}
                className="bg-slate-700 border-slate-600 text-white"
              />
            </div>
            <div>
              <Label className="text-slate-300">Notes</Label>
              <textarea
                value={refundNotes}
                onChange={(e) => setRefundNotes(e.target.value)}
                placeholder="Reason for partial refund, damage notes, etc."
                className="w-full h-20 bg-slate-700 border border-slate-600 rounded-lg p-2 text-white placeholder-slate-400 resize-none"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowRefundDialog(false)} className="text-slate-300">
              Cancel
            </Button>
            <Button onClick={handleRefund} disabled={processingRefund} className="bg-emerald-600 hover:bg-emerald-500">
              {processingRefund ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Process ${parseFloat(refundAmount || 0).toFixed(2)} Refund
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Settings Dialog */}
      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="bg-slate-800 border-slate-700 max-w-2xl max-h-[90vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Settings className="h-5 w-5" />
              Rental Settings
            </DialogTitle>
          </DialogHeader>
          <div className="overflow-y-auto max-h-[65vh] pr-2" style={{ WebkitOverflowScrolling: 'touch' }}>
            {editingSettings === null && settings && (
              <Button 
                onClick={() => setEditingSettings({...settings})}
                className="mb-4 bg-purple-600 hover:bg-purple-500"
              >
                <Edit2 className="h-4 w-4 mr-2" />
                Edit Settings
              </Button>
            )}
            
            {editingSettings ? (
              <div className="space-y-4 pr-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-300">Deposit Amount ($)</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={editingSettings.deposit_amount ?? ''}
                      onChange={(e) => setEditingSettings({...editingSettings, deposit_amount: e.target.value === '' ? '' : e.target.value})}
                      onBlur={(e) => {
                        const val = parseFloat(e.target.value);
                        if (isNaN(val) || val < 0) setEditingSettings(prev => ({...prev, deposit_amount: 0}));
                        else setEditingSettings(prev => ({...prev, deposit_amount: val}));
                      }}
                      className="bg-slate-700 border-slate-600 text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300">Delivery Fee ($)</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={editingSettings.delivery_fee ?? ''}
                      onChange={(e) => setEditingSettings({...editingSettings, delivery_fee: e.target.value === '' ? '' : e.target.value})}
                      onBlur={(e) => {
                        const val = parseFloat(e.target.value);
                        if (isNaN(val) || val < 0) setEditingSettings(prev => ({...prev, delivery_fee: 0}));
                        else setEditingSettings(prev => ({...prev, delivery_fee: val}));
                      }}
                      className="bg-slate-700 border-slate-600 text-white"
                      placeholder="0.00"
                    />
                    <p className="text-xs text-slate-400 mt-1">Shown to customer under deposit</p>
                  </div>
                </div>
                
                <div>
                  <Label className="text-slate-300">Terms and Conditions</Label>
                  <textarea
                    value={editingSettings.terms_and_conditions || ''}
                    onChange={(e) => setEditingSettings({...editingSettings, terms_and_conditions: e.target.value})}
                    className="w-full h-40 bg-slate-700 border border-slate-600 rounded-lg p-2 text-white placeholder-slate-400 resize-none"
                  />
                </div>
                
                <div>
                  <Label className="text-slate-300">Admin Notification Emails (comma separated)</Label>
                  <Input
                    value={editingSettings.admin_notification_emails?.join(', ') || ''}
                    onChange={(e) => setEditingSettings({
                      ...editingSettings, 
                      admin_notification_emails: e.target.value.split(',').map(s => s.trim()).filter(Boolean)
                    })}
                    placeholder="admin1@example.com, admin2@example.com"
                    className="bg-slate-700 border-slate-600 text-white"
                  />
                </div>
                
                <div>
                  <Label className="text-slate-300 mb-2 block">Pickup/Return Locations</Label>
                  <p className="text-slate-400 text-xs mb-2">Set a delivery fee for each location. Custom locations will use the default delivery fee above.</p>
                  {editingSettings.pickup_locations?.map((loc, index) => (
                    <LocationRow
                      key={loc.id || `new-loc-${index}`}
                      location={loc}
                      index={index}
                      locationsRef={locationRefsRef}
                      onDelete={(idx) => {
                        const locs = editingSettings.pickup_locations.filter((_, i) => i !== idx);
                        // Also clean up refs
                        delete locationRefsRef.current[idx];
                        // Reindex refs
                        const newRefs = {};
                        locs.forEach((_, i) => {
                          if (locationRefsRef.current[i > idx ? i + 1 : i]) {
                            newRefs[i] = locationRefsRef.current[i > idx ? i + 1 : i];
                          }
                        });
                        locationRefsRef.current = newRefs;
                        setEditingSettings({...editingSettings, pickup_locations: locs});
                      }}
                    />
                  ))}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      const newId = `loc-${Date.now()}`;
                      const locs = [...(editingSettings.pickup_locations || []), { id: newId, name: '', address: '', delivery_fee: 0 }];
                      setEditingSettings({...editingSettings, pickup_locations: locs});
                    }}
                    className="border-slate-600 text-slate-300"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add Location
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-3 text-sm">
                <p className="text-slate-300"><strong>Deposit:</strong> ${settings?.deposit_amount?.toFixed(2)}</p>
                <p className="text-slate-300"><strong>Default Delivery Fee (Custom Locations):</strong> ${settings?.delivery_fee?.toFixed(2) || '0.00'}</p>
                <div className="text-slate-300">
                  <strong>Locations:</strong>
                  <ul className="mt-1 ml-4 space-y-1">
                    {settings?.pickup_locations?.map((l, i) => (
                      <li key={i} className="text-slate-400">
                        {l.name} - ${(l.delivery_fee || 0).toFixed(2)} delivery
                      </li>
                    ))}
                  </ul>
                </div>
                <p className="text-slate-300"><strong>Admin Emails:</strong> {settings?.admin_notification_emails?.join(', ') || 'None'}</p>
              </div>
            )}
          </div>
          <DialogFooter>
            {editingSettings && (
              <>
                <Button variant="ghost" onClick={() => setEditingSettings(null)} className="text-slate-300">
                  Cancel
                </Button>
                <Button onClick={handleSaveSettings} disabled={savingSettings} className="bg-emerald-600 hover:bg-emerald-500">
                  {savingSettings ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Save Settings
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Add Rental Product Dialog */}
      <Dialog open={showAddProduct} onOpenChange={setShowAddProduct}>
        <DialogContent className="bg-slate-800 border-slate-700 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Plus className="h-5 w-5 text-green-400" />
              Add Rental Product
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Create a custom rental product that will appear on the rentals page
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label className="text-slate-300">Product Name *</Label>
              <Input
                value={newProduct.name}
                onChange={(e) => setNewProduct({...newProduct, name: e.target.value})}
                placeholder="e.g., Adult Manikin Rental"
                className="bg-slate-700 border-slate-600 text-white"
              />
            </div>
            
            <div>
              <Label className="text-slate-300">Price per Day ($) *</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={newProduct.price}
                onChange={(e) => setNewProduct({...newProduct, price: e.target.value})}
                placeholder="15.00"
                className="bg-slate-700 border-slate-600 text-white"
              />
            </div>
            
            <div>
              <Label className="text-slate-300">SKU (Optional)</Label>
              <Input
                value={newProduct.sku}
                onChange={(e) => setNewProduct({...newProduct, sku: e.target.value})}
                placeholder="rent-custom-manikin"
                className="bg-slate-700 border-slate-600 text-white"
              />
              <p className="text-xs text-slate-400 mt-1">Leave blank to auto-generate</p>
            </div>
            
            <div>
              <Label className="text-slate-300">Description (Optional)</Label>
              <textarea
                value={newProduct.description}
                onChange={(e) => setNewProduct({...newProduct, description: e.target.value})}
                placeholder="Brief description of the rental item..."
                className="w-full h-20 bg-slate-700 border border-slate-600 rounded-lg p-2 text-white placeholder-slate-400 resize-none"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowAddProduct(false)} className="text-slate-300">
              Cancel
            </Button>
            <Button 
              onClick={createRentalProduct} 
              disabled={savingProduct || !newProduct.name || !newProduct.price}
              className="bg-green-600 hover:bg-green-500"
            >
              {savingProduct ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
              Create Product
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Manual Order Dialog */}
      <Dialog open={showManualOrder} onOpenChange={setShowManualOrder}>
        <DialogContent className="bg-slate-800 border-slate-700 max-w-4xl max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Plus className="h-5 w-5 text-green-400" />
              Create Manual Rental Order
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Create a rental order for walk-in or phone customers
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto pr-2">
            <div className="grid grid-cols-2 gap-6 p-1">
              {/* Left Column - Customer & Schedule */}
              <div className="space-y-4">
                <div className="space-y-3">
                  <h4 className="text-sm font-medium text-slate-300 flex items-center gap-2">
                    <User className="h-4 w-4" />
                    Customer Information
                  </h4>
                  
                  <div>
                    <Label className="text-slate-400 text-xs">Instructor *</Label>
                    <Select 
                      value={manualOrder.instructorId}
                      onValueChange={(val) => {
                        const instructor = availableInstructors.find(i => i.id === val);
                        setManualOrder(prev => ({
                          ...prev,
                          instructorId: val,
                          customerName: instructor?.name || prev.customerName,
                          customerEmail: instructor?.email || prev.customerEmail,
                          customerPhone: instructor?.phone || prev.customerPhone
                        }));
                      }}
                    >
                      <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                        <SelectValue placeholder="Select instructor..." />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-700 border-slate-600">
                        {availableInstructors.map(inst => (
                          <SelectItem key={inst.id} value={inst.id} className="text-white">
                            {inst.name} ({inst.email})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label className="text-slate-400 text-xs">Customer Name *</Label>
                    <Input
                      value={manualOrder.customerName}
                      onChange={(e) => setManualOrder(prev => ({ ...prev, customerName: e.target.value }))}
                      className="bg-slate-700 border-slate-600 text-white"
                      placeholder="Full name"
                    />
                  </div>
                  
                  <div>
                    <Label className="text-slate-400 text-xs">Email *</Label>
                    <Input
                      type="email"
                      value={manualOrder.customerEmail}
                      onChange={(e) => setManualOrder(prev => ({ ...prev, customerEmail: e.target.value }))}
                      className="bg-slate-700 border-slate-600 text-white"
                      placeholder="email@example.com"
                    />
                  </div>
                  
                  <div>
                    <Label className="text-slate-400 text-xs">Phone</Label>
                    <Input
                      value={manualOrder.customerPhone}
                      onChange={(e) => setManualOrder(prev => ({ ...prev, customerPhone: e.target.value }))}
                      className="bg-slate-700 border-slate-600 text-white"
                      placeholder="(555) 555-5555"
                    />
                  </div>
                </div>
                
                <div className="space-y-3">
                  <h4 className="text-sm font-medium text-slate-300 flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    Schedule
                  </h4>
                  
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-slate-400 text-xs">Pickup Date *</Label>
                      <Input
                        type="date"
                        value={manualOrder.pickupDate}
                        onChange={(e) => setManualOrder(prev => ({ ...prev, pickupDate: e.target.value }))}
                        className="bg-slate-700 border-slate-600 text-white"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-400 text-xs">Pickup Time</Label>
                      <Input
                        type="time"
                        value={manualOrder.pickupTime}
                        onChange={(e) => setManualOrder(prev => ({ ...prev, pickupTime: e.target.value }))}
                        className="bg-slate-700 border-slate-600 text-white"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-slate-400 text-xs">Return Date *</Label>
                      <Input
                        type="date"
                        value={manualOrder.returnDate}
                        onChange={(e) => setManualOrder(prev => ({ ...prev, returnDate: e.target.value }))}
                        min={manualOrder.pickupDate}
                        className="bg-slate-700 border-slate-600 text-white"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-400 text-xs">Return Time</Label>
                      <Input
                        type="time"
                        value={manualOrder.returnTime}
                        onChange={(e) => setManualOrder(prev => ({ ...prev, returnTime: e.target.value }))}
                        className="bg-slate-700 border-slate-600 text-white"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-slate-400 text-xs">Pickup Location</Label>
                      <Select 
                        value={manualOrder.pickupLocation}
                        onValueChange={(val) => setManualOrder(prev => ({ ...prev, pickupLocation: val }))}
                      >
                        <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                          <SelectValue placeholder="Select..." />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-700 border-slate-600">
                          {settings?.pickup_locations?.map(loc => (
                            <SelectItem key={loc.id} value={loc.id} className="text-white">
                              {loc.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-slate-500 text-xs mt-1">Equipment will be returned to the same location</p>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-3">
                  <h4 className="text-sm font-medium text-slate-300 flex items-center gap-2">
                    <DollarSign className="h-4 w-4" />
                    Payment
                  </h4>
                  
                  <div>
                    <Label className="text-slate-400 text-xs">Payment Method</Label>
                    <Select 
                      value={manualOrder.paymentMethod}
                      onValueChange={(val) => setManualOrder(prev => ({ ...prev, paymentMethod: val }))}
                    >
                      <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-700 border-slate-600">
                        <SelectItem value="cash" className="text-white">Cash</SelectItem>
                        <SelectItem value="card" className="text-white">Card (In-Person)</SelectItem>
                        <SelectItem value="invoice" className="text-white">Invoice Later</SelectItem>
                        <SelectItem value="comp" className="text-white">Complimentary</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Deposit Amount Input */}
                  <div className="space-y-2">
                    <Label className="text-slate-400 text-xs">Deposit Amount</Label>
                    <div className="flex items-center gap-2">
                      <span className="text-slate-400">$</span>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={manualOrder.customDeposit}
                        onChange={(e) => setManualOrder(prev => ({ ...prev, customDeposit: e.target.value }))}
                        className="bg-slate-700 border-slate-600 text-white w-32"
                        placeholder="0.00"
                      />
                    </div>
                    <p className="text-slate-500 text-xs">Enter 0 for no deposit, or custom amount</p>
                  </div>
                  
                  <div>
                    <Label className="text-slate-400 text-xs">Notes</Label>
                    <textarea
                      value={manualOrder.notes}
                      onChange={(e) => setManualOrder(prev => ({ ...prev, notes: e.target.value }))}
                      className="w-full h-16 bg-slate-700 border border-slate-600 rounded-lg p-2 text-white placeholder-slate-400 resize-none text-sm"
                      placeholder="Any special notes..."
                    />
                  </div>
                </div>
              </div>
              
              {/* Right Column - Items */}
              <div className="space-y-4">
                <div className="space-y-3">
                  <h4 className="text-sm font-medium text-slate-300 flex items-center gap-2">
                    <Package className="h-4 w-4" />
                    Add Items
                  </h4>
                  
                  <div className="max-h-48 overflow-y-auto bg-slate-700/50 rounded-lg p-2 space-y-1">
                    {availableProducts.map(product => (
                      <div 
                        key={product.sku}
                        className="flex items-center justify-between p-2 bg-slate-800/50 rounded hover:bg-slate-800 cursor-pointer"
                        onClick={() => addItemToManualOrder(product)}
                      >
                        <div>
                          <p className="text-white text-sm">
                            {product.name}
                            {product.type === 'ecard' && (
                              <span className="ml-2 text-xs bg-blue-600/40 text-blue-300 px-1.5 py-0.5 rounded">eCard</span>
                            )}
                            {product.flat_rate && (
                              <span className="ml-2 text-xs bg-green-600/40 text-green-300 px-1.5 py-0.5 rounded">Flat</span>
                            )}
                          </p>
                          <p className="text-purple-400 text-xs">
                            ${product.price.toFixed(2)}{product.type === 'ecard' ? '' : (product.flat_rate ? ' (one-time)' : '/day')}
                            {product.type === 'ecard' && product.min_order > 1 && (
                              <span className="text-slate-400 ml-2">(min: {product.min_order})</span>
                            )}
                          </p>
                        </div>
                        <Plus className="h-4 w-4 text-green-400" />
                      </div>
                    ))}
                  </div>
                </div>
                
                <div className="space-y-3">
                  <h4 className="text-sm font-medium text-slate-300">Order Items</h4>
                  
                  {manualOrder.items.length === 0 ? (
                    <p className="text-slate-500 text-sm text-center py-4">No items added yet</p>
                  ) : (
                    <div className="space-y-2">
                      {manualOrder.items.map(item => (
                        <div key={item.sku} className="flex items-center justify-between p-2 bg-slate-700/50 rounded">
                          <div className="flex-1">
                            <p className="text-white text-sm">
                              {item.name}
                              {item.type === 'ecard' && (
                                <span className="ml-2 text-xs bg-blue-600/40 text-blue-300 px-1.5 py-0.5 rounded">eCard</span>
                              )}
                              {item.flat_rate && (
                                <span className="ml-2 text-xs bg-green-600/40 text-green-300 px-1.5 py-0.5 rounded">Flat</span>
                              )}
                            </p>
                            <p className="text-purple-400 text-xs">
                              ${item.unit_price.toFixed(2)}{item.type === 'ecard' ? '' : (item.flat_rate ? ' (one-time)' : '/day')}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => updateManualOrderItem(item.sku, item.quantity - 1)}
                              className="h-6 w-6 p-0 text-white"
                            >
                              <Minus className="h-3 w-3" />
                            </Button>
                            <span className="text-white w-6 text-center">{item.quantity}</span>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => updateManualOrderItem(item.sku, item.quantity + 1)}
                              className="h-6 w-6 p-0 text-white"
                            >
                              <Plus className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                
                {/* Order Summary */}
                {manualOrder.items.length > 0 && (
                  <div className="bg-purple-900/30 border border-purple-500/30 rounded-lg p-3 space-y-2">
                    <h4 className="text-sm font-medium text-purple-300">Order Summary</h4>
                    {(() => {
                      const totals = calculateManualOrderTotal();
                      return (
                        <>
                          <div className="flex justify-between text-sm">
                            <span className="text-slate-400">Duration:</span>
                            <span className="text-white">{totals.days} day{totals.days > 1 ? 's' : ''}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-slate-400">Subtotal:</span>
                            <span className="text-white">${totals.subtotal.toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-slate-400">Deposit:</span>
                            <span className="text-white">${totals.deposit.toFixed(2)}</span>
                          </div>
                          {totals.deliveryFee > 0 && (
                            <div className="flex justify-between text-sm">
                              <span className="text-slate-400">Delivery:</span>
                              <span className="text-white">${totals.deliveryFee.toFixed(2)}</span>
                            </div>
                          )}
                          <div className="flex justify-between text-sm font-bold pt-2 border-t border-purple-500/30">
                            <span className="text-purple-300">Total:</span>
                            <span className="text-purple-400">${totals.total.toFixed(2)}</span>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                )}
              </div>
            </div>
          </div>
          
          <DialogFooter className="border-t border-slate-700 pt-4">
            <Button variant="ghost" onClick={() => setShowManualOrder(false)} className="text-slate-300">
              Cancel
            </Button>
            <Button 
              onClick={createManualOrder} 
              disabled={savingManualOrder || manualOrder.items.length === 0}
              className="bg-green-600 hover:bg-green-500"
            >
              {savingManualOrder ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
              Create Order
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
    
    {/* Edit Schedule Dialog */}
    <Dialog open={showEditSchedule} onOpenChange={setShowEditSchedule}>
      <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-white">
            <Calendar className="h-5 w-5 text-green-400" />
            Edit Pickup/Return Schedule
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {/* Current Schedule Info */}
          {selectedOrder && (
            <div className="bg-slate-700/50 border border-slate-600 rounded-lg p-3 text-sm">
              <p className="text-slate-400 mb-1">Order: <span className="text-white">{selectedOrder.order_id}</span></p>
              <p className="text-slate-400">Customer: <span className="text-white">{selectedOrder.customer?.name}</span></p>
            </div>
          )}
          
          {/* Pickup Date/Time */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label className="text-slate-300">Pickup Date</Label>
              <Input
                type="date"
                value={editScheduleData.pickupDate}
                onChange={(e) => setEditScheduleData({...editScheduleData, pickupDate: e.target.value})}
                className="bg-slate-700 border-slate-600 text-white"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Pickup Time</Label>
              <Input
                type="time"
                value={editScheduleData.pickupTime}
                onChange={(e) => setEditScheduleData({...editScheduleData, pickupTime: e.target.value})}
                className="bg-slate-700 border-slate-600 text-white"
              />
            </div>
          </div>
          
          {/* Return Date/Time */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label className="text-slate-300">Return Date</Label>
              <Input
                type="date"
                value={editScheduleData.returnDate}
                onChange={(e) => setEditScheduleData({...editScheduleData, returnDate: e.target.value})}
                className="bg-slate-700 border-slate-600 text-white"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Return Time</Label>
              <Input
                type="time"
                value={editScheduleData.returnTime}
                onChange={(e) => setEditScheduleData({...editScheduleData, returnTime: e.target.value})}
                className="bg-slate-700 border-slate-600 text-white"
              />
            </div>
          </div>
          
          {/* Override Calendar Check */}
          <div className="flex items-center gap-3 bg-amber-900/30 border border-amber-600/30 rounded-lg p-3">
            <input
              type="checkbox"
              id="bypassCalendar"
              checked={editScheduleData.bypassCalendarCheck}
              onChange={(e) => setEditScheduleData({...editScheduleData, bypassCalendarCheck: e.target.checked})}
              className="h-4 w-4 rounded border-amber-500 bg-slate-700 text-amber-500 focus:ring-amber-500"
            />
            <div>
              <Label htmlFor="bypassCalendar" className="text-amber-300 cursor-pointer">
                Override Calendar Availability
              </Label>
              <p className="text-xs text-amber-200/60 mt-0.5">
                Allow scheduling even if time conflicts with existing calendar events
              </p>
            </div>
          </div>
          
          {/* Info about calendar sync */}
          <div className="bg-blue-900/30 border border-blue-500/30 rounded-lg p-3 text-xs text-blue-200/80">
            <p className="flex items-center gap-1">
              <RefreshCw className="h-3 w-3" />
              Changes will automatically update Google Calendar events
            </p>
          </div>
        </div>
        
        <DialogFooter>
          <Button 
            variant="ghost" 
            onClick={() => setShowEditSchedule(false)} 
            className="text-slate-300"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveSchedule}
            disabled={savingSchedule}
            className="bg-green-600 hover:bg-green-500"
          >
            {savingSchedule ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Check className="h-4 w-4 mr-2" />
            )}
            Save Schedule
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
};

export default RentalManagement;
